import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled, { createGlobalStyle, css } from 'styled-components';
import { Flex, Box } from '@rebass/grid';
import { getThemeProp, media, lighten } from '@amtrav/themes';
import { skeletonLoadingStyles, ActivityIndicator, PriceButton, formatUSCurrencyNum, IconFlex, Heading3, RefundabilityBadge, AddressRegular, AreaDistance, MetricTileMini, Text, Badge, PreferredBadge, CarTypeImage, CarVendorLogo, OutOfPolicy, OptionMini, EmptyState, Button } from '@amtrav/view-components';
import { IconGearShift, IconCarDoor, IconUser, IconSuitcaseLine, IconPlug, IconEco, IconCog, IconPlane } from '@amtrav/icons';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

var __assign = function () {
  __assign = Object.assign || function __assign(t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
function __read(o, n) {
  var m = typeof Symbol === "function" && o[Symbol.iterator];
  if (!m) return o;
  var i = m.call(o),
    r,
    ar = [],
    e;
  try {
    while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
  } catch (error) {
    e = {
      error: error
    };
  } finally {
    try {
      if (r && !r.done && (m = i["return"])) m.call(i);
    } finally {
      if (e) throw e.error;
    }
  }
  return ar;
}
function __spreadArray(to, from, pack) {
  if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
    if (ar || !(i in from)) {
      if (!ar) ar = Array.prototype.slice.call(from, 0, i);
      ar[i] = from[i];
    }
  }
  return to.concat(ar || Array.prototype.slice.call(from));
}
function __makeTemplateObject(cooked, raw) {
  if (Object.defineProperty) {
    Object.defineProperty(cooked, "raw", {
      value: raw
    });
  } else {
    cooked.raw = raw;
  }
  return cooked;
}
var THEME_TOKENS = {
  avatar: {
    color: {
      backgroundDefault: '--avatar-color-background-default',
      background1: '--avatar-color-background1',
      background2: '--avatar-color-background2',
      background3: '--avatar-color-background3',
      background4: '--avatar-color-background4',
      border: '--avatar-color-border'
    }
  },
  base: {
    color: {
      /**
       * Color for primary text blocks (e.g. title, headings)
       */
      primaryText: '--base-color-primary-text',
      /**
       * Color for secondary text blocks (e.g. body text, caption)
       */
      secondaryText: '--base-color-secondary-text',
      /**
       * Color for text on primary actions (e.g. links, buttons)
       */
      action: '--base-color-action',
      /**
       * DEPRECATED, use action
       */
      link: '--base-color-link',
      /**
       * DEPRECATED, use secondaryText
       */
      bodyText: '--base-color-body-text',
      /**
       * DEPRECATED, use secondaryText
       */
      bodyTextSoft: '--base-color-body-text-soft',
      siteBackground: '--base-color-site-background',
      sidebarBackground: '--base-color-sidebar-background',
      /**
       * Used by the skeleton loader
       */
      loaderStart: '--base-color-loader-start',
      loaderEnd: '--base-color-loader-end',
      loadingBar: '--base-color-loading-bar',
      dim1: '--base-color-dim1',
      dim2: '--base-color-dim2',
      dim3: '--base-color-dim3',
      dim4: '--base-color-dim4',
      disabled: '--base-color-disabled',
      overlayBackdrop: '--base-color-overlay-backdrop',
      neutral0: '--base-color-neutral0',
      neutral50: '--base-color-neutral50',
      neutral100: '--base-color-neutral100',
      neutral200: '--base-color-neutral200',
      neutral300: '--base-color-neutral300',
      neutral400: '--base-color-neutral400',
      neutral500: '--base-color-neutral500',
      neutral600: '--base-color-neutral600',
      neutral700: '--base-color-neutral700',
      neutral800: '--base-color-neutral800',
      neutral900: '--base-color-neutral900',
      neutral950: '--base-color-neutral950',
      /**
       * Deprecated use neutral palette
       */
      white: '--base-color-white',
      /**
       * Deprecated use neutral palette
       */
      gray50: '--base-color-gray50',
      /**
       * Deprecated use neutral palette
       */
      gray100: '--base-color-gray100',
      /**
       * Deprecated use neutral palette
       */
      gray200: '--base-color-gray200',
      /**
       * Deprecated use neutral palette
       */
      gray300: '--base-color-gray300',
      /**
       * Deprecated use neutral palette
       */
      gray400: '--base-color-gray400',
      /**
       * Deprecated use neutral palette
       */
      gray500: '--base-color-gray500',
      /**
       * Deprecated use neutral palette
       */
      gray600: '--base-color-gray600',
      /**
       * Deprecated use neutral palette
       */
      gray700: '--base-color-gray700',
      /**
       * Deprecated use neutral palette
       */
      gray800: '--base-color-gray800',
      /**
       * Deprecated use neutral palette
       */
      gray900: '--base-color-gray900',
      /**
       * Deprecated use neutral palette
       */
      gray950: '--base-color-gray950',
      neutral: '--base-color-neutral',
      neutralBackground: '--base-color-neutral-background',
      neutralBorder: '--base-color-neutral-border',
      neutralText: '--base-color-neutral-text',
      success: '--base-color-success',
      successBackground: '--base-color-success-background',
      successBorder: '--base-color-success-border',
      successText: '--base-color-success-text',
      warning: '--base-color-warning',
      warningBackground: '--base-color-warning-background',
      warningBorder: '--base-color-warning-border',
      warningText: '--base-color-warning-text',
      danger: '--base-color-danger',
      dangerBackground: '--base-color-danger-background',
      dangerBorder: '--base-color-danger-border',
      dangerText: '--base-color-danger-text',
      info: '--base-color-info',
      infoBackground: '--base-color-info-background',
      infoBorder: '--base-color-info-border',
      infoText: '--base-color-info-text'
    },
    size: {
      rootText: '--base-size-root-text',
      bodyText: '--base-size-body-text',
      bodyProminentText: '--base-size-body-prominent-text',
      detailText: '--base-size-detail-text',
      header1: '--base-size-header1',
      header2: '--base-size-header2',
      header3: '--base-size-header3',
      header4: '--base-size-header4',
      borderRadius: '--base-size-border-radius',
      formInputText: '--base-size-form-input-text',
      titleCaseLetterSpacing: '--base-size-title-case-letter-spacing',
      titleCaseHeaderLetterSpacing: '--base-size-title-case-header-letter-spacing'
    },
    fontFamily: {
      body: '--base-font-family-body',
      heading: '--base-font-family-heading'
    },
    fontWeight: {
      normal: '--base-font-weight-normal',
      semiBold: '--base-font-weight-semi-bold',
      bold: '--base-font-weight-bold'
    },
    shadow: {
      raised: '--base-shadow-raised',
      floating: '--base-shadow-floating'
    }
  },
  badge: {
    color: {
      neutral: '--badge-color-neutral',
      neutralBackground: '--badge-color-neutral-background',
      neutralText: '--badge-color-neutral-text',
      success: '--badge-color-success',
      successBackground: '--badge-color-success-background',
      successText: '--badge-color-success-text',
      warning: '--badge-color-warning',
      warningBackground: '--badge-color-warning-background',
      warningText: '--badge-color-warning-text',
      danger: '--badge-color-danger',
      dangerBackground: '--badge-color-danger-background',
      dangerText: '--badge-color-danger-text',
      info: '--badge-color-info',
      infoBackground: '--badge-color-info-background',
      infoText: '--badge-color-info-text'
    },
    size: {
      text: '--badge-size-text',
      fontWeight: '--badge-size-font-weight',
      letterSpacing: '--badge-size-letter-spacing'
    }
  },
  button: {
    color: {
      disabledBackground: '--button-color-disabled',
      disabledText: '--button-color-disabled-text',
      disabledBorder: '--button-color-disabled-border'
    },
    size: {
      borderRadius: '--button-size-border-radius',
      border: '--button-size-border',
      small: '--button-size-small',
      form: '--button-size-form',
      medium: '--button-size-medium',
      large: '--button-size-large'
    }
  },
  card: {
    color: {
      border: '--card-color-border',
      background: '--card-color-background',
      backgroundSecondary: '--card-color-background-secondary'
    },
    size: {
      borderRadiusSmall: '--card-size-border-radius-small',
      borderRadiusMedium: '--card-size-border-radius-medium',
      borderRadiusLarge: '--card-size-border-radius-large'
    }
  },
  navigation: {
    color: {
      headingText: '--navigation-color-heading-text',
      link: '--navigation-color-link',
      linkSelected: '--navigation-color-link-selected',
      // the main background color of the navigation will eventually come from here
      // for now it is more efficient to have it load through legacy CSS
      background: '--navigation-color-background',
      sectionBackground: '--navigation-color-section-background',
      footerBackground: '--navigation-color-footer-background',
      helpCenterText: '--navigation-color-help-center-text',
      helpCenterBackground: '--navigation-color-help-center-background',
      hamburgerMobile: '--navigation-color-hamburger-mobile',
      // this is also referenced in AM5's legacy CSS
      border: '--navigation-color-border'
    }
  },
  notice: {
    color: {
      neutralBackground: '--notice-color-neutral-background',
      neutralBorder: '--notice-color-neutral-border',
      neutralText: '--notice-color-neutral-text',
      successBackground: '--notice-color-success-background',
      successBorder: '--notice-color-success-border',
      successText: '--notice-color-success-text',
      warningBackground: '--notice-color-warning-background',
      warningBorder: '--notice-color-warning-border',
      warningText: '--notice-color-warning-text',
      dangerBackground: '--notice-color-danger-background',
      dangerBorder: '--notice-color-danger-border',
      dangerText: '--notice-color-danger-text',
      infoBackground: '--notice-color-info-background',
      infoBorder: '--notice-color-info-border',
      infoText: '--notice-color-info-text'
    },
    size: {
      text: '--notice-size-text',
      fontWeight: '--notice-size-font-weight'
    }
  },
  flightOptions: {
    color: {
      airlineCardAirlineName: '--flight-options-color-airline-card-airline-name'
    }
  },
  textInput: {
    color: {
      border: '--text-input-color-border',
      disabled: '--text-input-color-disabled',
      disabledBorder: '--text-input-color-disabled-border',
      disabledText: '--text-input-color-disabled-text',
      background: '--text-input-color-background',
      backgroundHover: '--text-input-color-background-hover',
      placeholderText: '--text-input-color-placeholder-text'
    },
    shadow: {
      /** applied when input is focused */
      focus: '--text-input-shadow-focus'
    },
    size: {
      border: '--text-input-size-border',
      borderRadius: '--text-input-size-border-radius',
      text: '--text-input-size-text',
      minHeight: '--text-input-size-min-height'
    }
  }
};
var getTokenValue = function (token) {
  return "var(".concat(token, ")");
};
var SPACE = {
  size0: '0px',
  size2: '2px',
  size4: '4px',
  size6: '6px',
  size8: '8px',
  size12: '12px',
  size16: '16px',
  size24: '24px',
  size32: '32px',
  size48: '48px',
  size64: '64px',
  size96: '96px',
  size128: '128px'
};
var GENERIC_COLORS = {
  // White
  white: '#FFFFFF',
  blue50: '#EFF6FF',
  // background
  blue100: '#DBEAFE',
  blue200: '#BFDBFE',
  blue300: '#93C5FD',
  // border
  blue400: '#60A5FA',
  blue500: '#3B82F6',
  blue600: '#2563EB',
  blue700: '#1D4ED8',
  // info
  blue800: '#1E40AF',
  blue900: '#1A3276',
  // text
  blue950: '#172554',
  // Cyan
  cyan50: '#ECFEFF',
  cyan100: '#CFFAFE',
  cyan200: '#A5F3FC',
  cyan300: '#67E8F9',
  cyan400: '#22D3EE',
  cyan500: '#06B6D4',
  cyan600: '#0891B2',
  cyan700: '#0E7490',
  cyan800: '#155E75',
  cyan900: '#164E63',
  cyan950: '#083344',
  // Green
  green50: '#F0FDF4',
  // background
  green100: '#DCFCE7',
  green200: '#BBF7D0',
  green300: '#86EFAC',
  // border
  green400: '#4ADE80',
  green500: '#22C55E',
  // success
  green600: '#16A34A',
  green700: '#15803D',
  green800: '#166534',
  green900: '#14532D',
  // text
  green950: '#052E16',
  // gray
  gray50: '#fafafa',
  gray100: '#f7f7f7',
  // background
  gray200: '#f5f5f5',
  gray300: '#eeeeee',
  gray400: '#d6d6d6',
  gray500: '#c0c0c0',
  // neutral & border
  gray600: '#9e9e9e',
  gray700: '#757575',
  gray800: '#616161',
  gray900: '#424242',
  // text
  gray950: '#212121',
  // Orange
  orange50: '#FFFBEB',
  // background
  orange100: '#FEF3C7',
  orange200: '#FDE68A',
  orange300: '#FCD34D',
  // border
  orange400: '#FBBF24',
  orange500: '#F59E0B',
  orange600: '#D97706',
  // warning
  orange700: '#B45309',
  orange800: '#92400E',
  orange900: '#78350F',
  // text
  orange950: '#451A03',
  // Purple
  purple50: '#F5F3FF',
  purple100: '#EDE9FE',
  purple200: '#DDD6FE',
  purple300: '#D8B4FE',
  purple400: '#C084FC',
  purple500: '#A855F7',
  purple600: '#9333EA',
  purple700: '#7E22CE',
  purple800: '#6B21A8',
  purple900: '#581C87',
  purple950: '#3B0764',
  // Red
  red50: '#FEF2F2',
  // background
  red100: '#FEE2E2',
  red200: '#FECACA',
  red300: '#FCA5A5',
  // border
  red400: '#F87171',
  red500: '#EF4444',
  // danger
  red600: '#DC2626',
  red700: '#B91C1C',
  red800: '#991B1B',
  red900: '#7F1D1D',
  // text
  red950: '#450A0A'
};
var THEME_CONFIG = {
  base: {
    id: {
      logoInterstitial: 'config_base_id_logoInterstitial'
    },
    flag: {
      /**
       * indicated whether the theme is our flagship theme
       */
      isFlagshipSite: 'config_base_flag_isFlagshipSite'
    }
  },
  navigation: {
    flag: {
      showPoweredByAmtrav: 'config_navigation_flag_showPoweredByAmtrav'
    },
    id: {
      logo: 'config_navigation_id_logo',
      mode: 'config_navigation_id_mode'
    }
  }
};
var rootTheme = {
  themeSystemName: 'root',
  themeSystemConfigMap: new Map([[THEME_CONFIG.base.id.logoInterstitial, ''], [THEME_CONFIG.base.flag.isFlagshipSite, false], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, false], [THEME_CONFIG.navigation.id.logo, ''], [THEME_CONFIG.navigation.id.mode, 'dark']]),
  themeSystemCssPropertiesMap: new Map([
  // avatar
  [THEME_TOKENS.avatar.color.backgroundDefault, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.background1, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.background2, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.background3, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.background4, GENERIC_COLORS.gray300], [THEME_TOKENS.avatar.color.border, GENERIC_COLORS.gray500],
  // base
  [THEME_TOKENS.base.color.primaryText, GENERIC_COLORS.gray950], [THEME_TOKENS.base.color.secondaryText, GENERIC_COLORS.gray950], [THEME_TOKENS.base.color.action, GENERIC_COLORS.blue500], /* deprecated */[THEME_TOKENS.base.color.link, getTokenValue(THEME_TOKENS.base.color.action)], /* deprecated */[THEME_TOKENS.base.color.bodyText, getTokenValue(THEME_TOKENS.base.color.secondaryText)], /* deprecated */[THEME_TOKENS.base.color.bodyTextSoft, getTokenValue(THEME_TOKENS.base.color.secondaryText)], [THEME_TOKENS.base.color.siteBackground, GENERIC_COLORS.gray100], [THEME_TOKENS.base.color.sidebarBackground, 'transparent'], [THEME_TOKENS.base.color.loaderStart, '#d4dbe48c'], [THEME_TOKENS.base.color.loaderEnd, '#d4dbe4'], [THEME_TOKENS.base.color.loadingBar, THEME_TOKENS.base.color.action], [THEME_TOKENS.base.color.dim1, GENERIC_COLORS.gray100], [THEME_TOKENS.base.color.dim2, GENERIC_COLORS.gray300], [THEME_TOKENS.base.color.dim3, GENERIC_COLORS.gray400], [THEME_TOKENS.base.color.dim4, GENERIC_COLORS.gray500], [THEME_TOKENS.base.color.disabled, GENERIC_COLORS.gray200], [THEME_TOKENS.base.color.overlayBackdrop, 'rgba(25, 25, 15, 0.2)'], /* deprecated */[THEME_TOKENS.base.color.white, GENERIC_COLORS.white], /* deprecated */[THEME_TOKENS.base.color.gray50, GENERIC_COLORS.gray50], /* deprecated */[THEME_TOKENS.base.color.gray100, GENERIC_COLORS.gray100], /* deprecated */[THEME_TOKENS.base.color.gray200, GENERIC_COLORS.gray200], /* deprecated */[THEME_TOKENS.base.color.gray300, GENERIC_COLORS.gray300], /* deprecated */[THEME_TOKENS.base.color.gray400, GENERIC_COLORS.gray400], /* deprecated */[THEME_TOKENS.base.color.gray500, GENERIC_COLORS.gray500], /* deprecated */[THEME_TOKENS.base.color.gray600, GENERIC_COLORS.gray600], /* deprecated */[THEME_TOKENS.base.color.gray700, GENERIC_COLORS.gray700], /* deprecated */[THEME_TOKENS.base.color.gray800, GENERIC_COLORS.gray800], /* deprecated */[THEME_TOKENS.base.color.gray900, GENERIC_COLORS.gray900], /* deprecated */[THEME_TOKENS.base.color.gray950, GENERIC_COLORS.gray950], [THEME_TOKENS.base.color.neutral0, GENERIC_COLORS.white], [THEME_TOKENS.base.color.neutral50, GENERIC_COLORS.gray50], [THEME_TOKENS.base.color.neutral100, GENERIC_COLORS.gray100], [THEME_TOKENS.base.color.neutral200, GENERIC_COLORS.gray200], [THEME_TOKENS.base.color.neutral300, GENERIC_COLORS.gray300], [THEME_TOKENS.base.color.neutral400, GENERIC_COLORS.gray400], [THEME_TOKENS.base.color.neutral500, GENERIC_COLORS.gray500], [THEME_TOKENS.base.color.neutral600, GENERIC_COLORS.gray600], [THEME_TOKENS.base.color.neutral700, GENERIC_COLORS.gray700], [THEME_TOKENS.base.color.neutral800, GENERIC_COLORS.gray800], [THEME_TOKENS.base.color.neutral900, GENERIC_COLORS.gray900], [THEME_TOKENS.base.color.neutral950, GENERIC_COLORS.gray950],
  // neutral
  [THEME_TOKENS.base.color.neutral, GENERIC_COLORS.gray500], [THEME_TOKENS.base.color.neutralBackground, GENERIC_COLORS.gray50], [THEME_TOKENS.base.color.neutralBorder, GENERIC_COLORS.gray400], [THEME_TOKENS.base.color.neutralText, GENERIC_COLORS.gray900],
  // success
  [THEME_TOKENS.base.color.success, GENERIC_COLORS.green500], [THEME_TOKENS.base.color.successBackground, GENERIC_COLORS.green100], [THEME_TOKENS.base.color.successBorder, GENERIC_COLORS.green300], [THEME_TOKENS.base.color.successText, GENERIC_COLORS.green950],
  // warning
  [THEME_TOKENS.base.color.warning, GENERIC_COLORS.orange600], [THEME_TOKENS.base.color.warningBackground, GENERIC_COLORS.orange100], [THEME_TOKENS.base.color.warningBorder, GENERIC_COLORS.orange300], [THEME_TOKENS.base.color.warningText, GENERIC_COLORS.orange950],
  // danger
  [THEME_TOKENS.base.color.danger, GENERIC_COLORS.red500], [THEME_TOKENS.base.color.dangerBackground, GENERIC_COLORS.red50], [THEME_TOKENS.base.color.dangerBorder, GENERIC_COLORS.red300], [THEME_TOKENS.base.color.dangerText, GENERIC_COLORS.red950],
  // info
  [THEME_TOKENS.base.color.info, GENERIC_COLORS.blue700], [THEME_TOKENS.base.color.infoBackground, GENERIC_COLORS.blue50], [THEME_TOKENS.base.color.infoBorder, GENERIC_COLORS.blue300], [THEME_TOKENS.base.color.infoText, GENERIC_COLORS.blue950],
  // base.size
  [THEME_TOKENS.base.size.rootText, '14px'], [THEME_TOKENS.base.size.bodyText, getTokenValue(THEME_TOKENS.base.size.rootText)], [THEME_TOKENS.base.size.bodyProminentText, '16px'], [THEME_TOKENS.base.size.detailText, '12px'], [THEME_TOKENS.base.size.header1, '24px'], [THEME_TOKENS.base.size.header2, '22px'], [THEME_TOKENS.base.size.header3, '20px'], [THEME_TOKENS.base.size.header4, '16px'], [THEME_TOKENS.base.size.borderRadius, SPACE.size6], [THEME_TOKENS.base.size.formInputText, '14px'], [THEME_TOKENS.base.size.titleCaseLetterSpacing, '1.2px'], [THEME_TOKENS.base.size.titleCaseHeaderLetterSpacing, '1.83px'],
  // base.fontFamily
  [THEME_TOKENS.base.fontFamily.body, '"Source Sans Pro", system-ui, Arial, sans-serif'], [THEME_TOKENS.base.fontFamily.heading, getTokenValue(THEME_TOKENS.base.fontFamily.body)],
  // base.fontWeight
  [THEME_TOKENS.base.fontWeight.normal, '400'], [THEME_TOKENS.base.fontWeight.semiBold, '600'], [THEME_TOKENS.base.fontWeight.bold, '700'],
  // base.shadow
  [THEME_TOKENS.base.shadow.raised, '0px 3px 12px 0px rgba(0, 0, 0, 0.08), 0px 1px 6px 0px rgba(0, 0, 0, 0.08), 0px 0px 2px 0px rgba(0, 0, 0, 0.12)'], [THEME_TOKENS.base.shadow.floating, '0px 1px 1px 0px rgba(0, 0, 0, 0.05), 0px 6px 8px 0px rgba(0, 0, 0, 0.06), 0px 3px 3px 0px rgba(0, 0, 0, 0.035)'],
  // badge.color
  // neutral
  [THEME_TOKENS.badge.color.neutral, getTokenValue(THEME_TOKENS.base.color.neutral)], [THEME_TOKENS.badge.color.neutralBackground, GENERIC_COLORS.gray200], [THEME_TOKENS.badge.color.neutralText, GENERIC_COLORS.gray800],
  // success
  [THEME_TOKENS.badge.color.success, getTokenValue(THEME_TOKENS.base.color.success)], [THEME_TOKENS.badge.color.successBackground, getTokenValue(THEME_TOKENS.base.color.successBackground)], [THEME_TOKENS.badge.color.successText, getTokenValue(THEME_TOKENS.base.color.successText)],
  // warning
  [THEME_TOKENS.badge.color.warning, getTokenValue(THEME_TOKENS.base.color.warning)], [THEME_TOKENS.badge.color.warningBackground, getTokenValue(THEME_TOKENS.base.color.warningBackground)], [THEME_TOKENS.badge.color.warningText, getTokenValue(THEME_TOKENS.base.color.warningText)],
  // danger
  [THEME_TOKENS.badge.color.danger, getTokenValue(THEME_TOKENS.base.color.danger)], [THEME_TOKENS.badge.color.dangerBackground, GENERIC_COLORS.red100], [THEME_TOKENS.badge.color.dangerText, getTokenValue(THEME_TOKENS.base.color.dangerText)],
  // info
  [THEME_TOKENS.badge.color.info, getTokenValue(THEME_TOKENS.base.color.info)], [THEME_TOKENS.badge.color.infoBackground, getTokenValue(THEME_TOKENS.base.color.infoBackground)], [THEME_TOKENS.badge.color.infoText, getTokenValue(THEME_TOKENS.base.color.infoText)],
  // badge.size
  [THEME_TOKENS.badge.size.text, '14px'], [THEME_TOKENS.badge.size.fontWeight, getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)], [THEME_TOKENS.badge.size.letterSpacing, '0.8px'],
  // button.color
  [THEME_TOKENS.button.color.disabledBackground, getTokenValue(THEME_TOKENS.base.color.disabled)], [THEME_TOKENS.button.color.disabledBorder, getTokenValue(THEME_TOKENS.base.color.disabled)], [THEME_TOKENS.button.color.disabledText, GENERIC_COLORS.gray600],
  // button.size
  [THEME_TOKENS.button.size.borderRadius, SPACE.size4], [THEME_TOKENS.button.size.border, '1px'], [THEME_TOKENS.button.size.small, '14px'], [THEME_TOKENS.button.size.form, getTokenValue(THEME_TOKENS.base.size.formInputText)], [THEME_TOKENS.button.size.medium, SPACE.size16], [THEME_TOKENS.button.size.large, SPACE.size24],
  // card.color
  [THEME_TOKENS.card.color.border, getTokenValue(THEME_TOKENS.base.color.dim3)], [THEME_TOKENS.card.color.background, getTokenValue(THEME_TOKENS.base.color.dim1)], [THEME_TOKENS.card.color.backgroundSecondary, getTokenValue(THEME_TOKENS.base.color.dim2)],
  // card.size
  [THEME_TOKENS.card.size.borderRadiusSmall, SPACE.size4], [THEME_TOKENS.card.size.borderRadiusMedium, SPACE.size8], [THEME_TOKENS.card.size.borderRadiusLarge, SPACE.size12],
  // navigation.color
  [THEME_TOKENS.navigation.color.headingText, GENERIC_COLORS.blue300], [THEME_TOKENS.navigation.color.link, getTokenValue(THEME_TOKENS.base.color.neutral0)], [THEME_TOKENS.navigation.color.linkSelected, getTokenValue(THEME_TOKENS.base.color.neutral0)],
  // the main background color of the navigation will eventually come from here
  // for now it is more efficient to have it load through legacy CSS
  [THEME_TOKENS.navigation.color.background, getTokenValue(THEME_TOKENS.base.color.neutral950)], [THEME_TOKENS.navigation.color.sectionBackground, 'rgba(0, 0, 0, 0.085)'], [THEME_TOKENS.navigation.color.footerBackground, 'rgba(0, 0, 0, 0.4)'], [THEME_TOKENS.navigation.color.helpCenterText, getTokenValue(THEME_TOKENS.base.color.neutral0)], [THEME_TOKENS.navigation.color.helpCenterBackground, 'rgba(255, 255, 255, 0.3)'], [THEME_TOKENS.navigation.color.hamburgerMobile, getTokenValue(THEME_TOKENS.base.color.neutral0)], [THEME_TOKENS.navigation.color.border, 'rgba(130, 130, 130, 0.35)'],
  // notice.color
  // neutral
  [THEME_TOKENS.notice.color.neutralBackground, getTokenValue(THEME_TOKENS.base.color.neutralBackground)], [THEME_TOKENS.notice.color.neutralBorder, getTokenValue(THEME_TOKENS.base.color.neutralBorder)], [THEME_TOKENS.notice.color.neutralText, getTokenValue(THEME_TOKENS.base.color.neutralText)],
  // success
  [THEME_TOKENS.notice.color.successBackground, getTokenValue(THEME_TOKENS.base.color.successBackground)], [THEME_TOKENS.notice.color.successBorder, getTokenValue(THEME_TOKENS.base.color.successBorder)], [THEME_TOKENS.notice.color.successText, getTokenValue(THEME_TOKENS.base.color.successText)],
  // warning
  [THEME_TOKENS.notice.color.warningBackground, getTokenValue(THEME_TOKENS.base.color.warningBackground)], [THEME_TOKENS.notice.color.warningBorder, getTokenValue(THEME_TOKENS.base.color.warningBorder)], [THEME_TOKENS.notice.color.warningText, getTokenValue(THEME_TOKENS.base.color.warningText)],
  // danger
  [THEME_TOKENS.notice.color.dangerBackground, getTokenValue(THEME_TOKENS.base.color.dangerBackground)], [THEME_TOKENS.notice.color.dangerBorder, getTokenValue(THEME_TOKENS.base.color.dangerBorder)], [THEME_TOKENS.notice.color.dangerText, getTokenValue(THEME_TOKENS.base.color.dangerText)],
  // info
  [THEME_TOKENS.notice.color.infoBackground, getTokenValue(THEME_TOKENS.base.color.infoBackground)], [THEME_TOKENS.notice.color.infoBorder, getTokenValue(THEME_TOKENS.base.color.infoBorder)], [THEME_TOKENS.notice.color.infoText, getTokenValue(THEME_TOKENS.base.color.infoText)],
  // notice.size
  [THEME_TOKENS.notice.size.text, '16px'], [THEME_TOKENS.notice.size.fontWeight, getTokenValue(THEME_TOKENS.base.fontWeight.semiBold)],
  // flightOptions.color
  [THEME_TOKENS.flightOptions.color.airlineCardAirlineName, getTokenValue(THEME_TOKENS.base.color.action)],
  // textInput.color
  [THEME_TOKENS.textInput.color.border, getTokenValue(THEME_TOKENS.base.color.dim3)], [THEME_TOKENS.textInput.color.disabled, getTokenValue(THEME_TOKENS.base.color.disabled)], [THEME_TOKENS.textInput.color.disabledBorder, getTokenValue(THEME_TOKENS.base.color.disabled)], [THEME_TOKENS.textInput.color.disabledText, GENERIC_COLORS.gray600], [THEME_TOKENS.textInput.color.background, getTokenValue(THEME_TOKENS.base.color.neutral0)], [THEME_TOKENS.textInput.color.backgroundHover, getTokenValue(THEME_TOKENS.base.color.dim1)], [THEME_TOKENS.textInput.color.placeholderText, GENERIC_COLORS.gray600],
  // textInput.shadow
  [THEME_TOKENS.textInput.shadow.focus, 'none'],
  // textInput.size
  [THEME_TOKENS.textInput.size.border, '1px'], [THEME_TOKENS.textInput.size.borderRadius, SPACE.size4], [THEME_TOKENS.textInput.size.text, getTokenValue(THEME_TOKENS.base.size.formInputText)], [THEME_TOKENS.textInput.size.minHeight, '38px']])
};
var BOUNCE_NEXT_COLORS = {
  blue50: '#f1f7fd',
  blue75: '#E7F1FD',
  blue100: '#dcebfc',
  blue200: '#cfe0f8',
  blue300: '#a1ccfd',
  blue400: '#59a3fd',
  /**
   * primary color
   */
  blue500: '#0776fc',
  blue600: '#1054c1',
  blue700: '#073e97',
  blue800: '#07347e',
  blue900: '#132b6c',
  blue950: '#15203d',
  // Cyan
  cyan50: '#f2fcfd',
  cyan100: '#d8f9fd',
  cyan200: '#c5f6fc',
  cyan300: '#a6edf6',
  cyan400: '#87e5f0',
  cyan500: '#60cfdc',
  cyan600: '#1fb9ca',
  cyan700: '#0fa5b6',
  cyan800: '#0c8592',
  cyan900: '#0f5961',
  cyan950: '#0b4147',
  // Green
  green50: '#e8f8f2',
  green100: '#b3f0da',
  green200: '#a0e5cc',
  green300: '#62daaf',
  green400: '#00cd83',
  green500: '#05b777',
  green600: '#039761',
  green700: '#067e53',
  green800: '#036d47',
  green900: '#005234',
  green950: '#003d27',
  // gray
  gray50: '#fafafa',
  gray100: '#f7f7f7',
  gray200: '#f5f5f5',
  gray300: '#eeeeee',
  gray400: '#d6d6d6',
  gray500: '#c0c0c0',
  gray600: '#9e9e9e',
  gray700: '#757575',
  gray800: '#616161',
  gray900: '#424242',
  /**
   * Also used as a text color
   */
  gray950: '#212121',
  // Orange
  orange50: '#fffaf2',
  orange100: '#fcefdf',
  orange200: '#f8ddb9',
  orange300: '#f9d09a',
  orange400: '#fbc774',
  orange500: '#fcb727',
  orange600: '#e68e16',
  orange700: '#be730e',
  orange800: '#905504',
  orange900: '#5c3909',
  orange950: '#3a2303',
  // Purple
  purple50: '#f3f4fd',
  purple100: '#e1e0fa',
  purple200: '#d0cff7',
  purple300: '#c1c0f7',
  purple400: '#b2a8f5',
  purple500: '#a089dc',
  purple600: '#9673e7',
  purple700: '#7b4dcc',
  purple800: '#6631af',
  purple900: '#502a7e',
  purple950: '#3c1d63',
  // Red
  red50: '#fdeded',
  red100: '#f9cdcd',
  red200: '#f49f9f',
  red300: '#ef7676',
  red400: '#eb5757',
  red500: '#e73636',
  red600: '#d61919',
  red700: '#b21515',
  red800: '#8d1111',
  red900: '#640c0c',
  red950: '#490909'
};
var bounceNextTheme = {
  themeSystemName: 'bouncenext',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'BounceNextInterstitial'], [THEME_CONFIG.navigation.id.logo, 'BounceNextNavigation']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // base
  [THEME_TOKENS.base.color.primaryText, BOUNCE_NEXT_COLORS.blue950], [THEME_TOKENS.base.color.action, BOUNCE_NEXT_COLORS.blue500], [THEME_TOKENS.base.color.loadingBar, BOUNCE_NEXT_COLORS.orange500], [THEME_TOKENS.base.color.dim1, BOUNCE_NEXT_COLORS.blue50], [THEME_TOKENS.base.color.dim2, BOUNCE_NEXT_COLORS.blue100], [THEME_TOKENS.base.color.dim3, BOUNCE_NEXT_COLORS.blue200], [THEME_TOKENS.base.color.dim4, BOUNCE_NEXT_COLORS.blue300], [THEME_TOKENS.base.color.success, BOUNCE_NEXT_COLORS.green400], [THEME_TOKENS.base.color.successBackground, BOUNCE_NEXT_COLORS.green50], [THEME_TOKENS.base.color.successBorder, BOUNCE_NEXT_COLORS.green100], [THEME_TOKENS.base.color.successText, BOUNCE_NEXT_COLORS.green900], [THEME_TOKENS.base.color.warning, BOUNCE_NEXT_COLORS.orange600], [THEME_TOKENS.base.color.warningBackground, BOUNCE_NEXT_COLORS.orange100], [THEME_TOKENS.base.color.warningBorder, BOUNCE_NEXT_COLORS.orange400], [THEME_TOKENS.base.color.warningText, BOUNCE_NEXT_COLORS.orange900], [THEME_TOKENS.base.color.danger, BOUNCE_NEXT_COLORS.red500], [THEME_TOKENS.base.color.dangerBackground, BOUNCE_NEXT_COLORS.red50], [THEME_TOKENS.base.color.dangerBorder, BOUNCE_NEXT_COLORS.red200], [THEME_TOKENS.base.color.dangerText, BOUNCE_NEXT_COLORS.red900], [THEME_TOKENS.base.color.info, BOUNCE_NEXT_COLORS.blue700], [THEME_TOKENS.base.color.infoBackground, BOUNCE_NEXT_COLORS.blue50], [THEME_TOKENS.base.color.infoBorder, BOUNCE_NEXT_COLORS.blue200], [THEME_TOKENS.base.color.infoText, BOUNCE_NEXT_COLORS.blue900], [THEME_TOKENS.base.fontFamily.body, '"Public Sans", Helvetica, Arial, sans-serif'],
  // size
  [THEME_TOKENS.base.size.borderRadius, SPACE.size0],
  // badge
  [THEME_TOKENS.badge.color.warningBackground, BOUNCE_NEXT_COLORS.orange100],
  // button
  [THEME_TOKENS.button.size.borderRadius, SPACE.size32], [THEME_TOKENS.button.size.border, SPACE.size2],
  // textInput
  [THEME_TOKENS.textInput.size.border, SPACE.size2], [THEME_TOKENS.textInput.size.borderRadius, '0']], false))
};
({
  themeSystemName: 'a2b',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(bounceNextTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'A2bInterstitial'], [THEME_CONFIG.navigation.id.logo, 'A2bNavigation']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray([], __read(bounceNextTheme.themeSystemCssPropertiesMap), false))
});
({
  themeSystemName: 'atom',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(bounceNextTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'ATOMInterstitial'], [THEME_CONFIG.navigation.id.logo, 'ATOMNavigation']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(bounceNextTheme.themeSystemCssPropertiesMap), false), [
  // base
  [THEME_TOKENS.base.fontFamily.body, 'Roboto, Helvetica, Arial, sans-serif'], [THEME_TOKENS.base.fontWeight.semiBold, '500'], [THEME_TOKENS.base.color.primaryText, BOUNCE_NEXT_COLORS.gray950], [THEME_TOKENS.base.color.secondaryText, BOUNCE_NEXT_COLORS.gray800], [THEME_TOKENS.base.color.infoBorder, BOUNCE_NEXT_COLORS.gray400], [THEME_TOKENS.base.color.success, BOUNCE_NEXT_COLORS.green600], [THEME_TOKENS.base.color.successBorder, BOUNCE_NEXT_COLORS.green300],
  // size
  [THEME_TOKENS.base.size.borderRadius, SPACE.size6], [THEME_TOKENS.base.size.formInputText, '14px'], [THEME_TOKENS.base.size.header3, '18px'],
  // button
  [THEME_TOKENS.button.size.borderRadius, SPACE.size4], [THEME_TOKENS.button.size.small, SPACE.size12], [THEME_TOKENS.button.size.border, '1px'],
  // card
  [THEME_TOKENS.card.color.border, BOUNCE_NEXT_COLORS.gray400],
  // textInput
  [THEME_TOKENS.textInput.size.border, '1px'], [THEME_TOKENS.textInput.color.border, BOUNCE_NEXT_COLORS.gray500], [THEME_TOKENS.textInput.size.borderRadius, SPACE.size4], [THEME_TOKENS.textInput.size.minHeight, '26px'], [THEME_TOKENS.textInput.color.backgroundHover, BOUNCE_NEXT_COLORS.blue75]], false))
});
var ADVISOR_CONSOLE_COLORS = {
  lightBlueMist: '#eff9ff',
  // card
  lightBlue: '#9bd1e4',
  cgBlue: '#007da5',
  darkBlue: '#165d85',
  cyan: '#21a9cd',
  amazonite: '#12c5b2',
  mantisGreen: '#75cb44',
  cherry: '#db3260',
  libertyPurple: '#6353a3',
  backgroundCream: '#fffeed',
  // textfield
  grainYellow: '#F7D45E',
  // NOTICE
  success: '#4da129',
  successBackground: '#edf6ea',
  successBorder: '#bbdbae',
  successText: '#204411',
  warning: '#e68e16',
  warningBackground: '#fdf4e8',
  warningBorder: '#f6d4a6',
  warningText: '#613c09',
  danger: '#a12929',
  dangerBackground: '#f6eaea',
  dangerBorder: '#dbaeae',
  dangerText: '#441111',
  blue: '#156da8',
  // info
  infoBackground: '#e8f0f6',
  infoBorder: '#a6c8de',
  infoText: '#092e47',
  // BADGE
  successBackgroundBadge: '#defbd0',
  warningBackgroundBadge: '#ffecd2'
};
({
  themeSystemName: 'advisorconsole',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, '']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, ADVISOR_CONSOLE_COLORS.darkBlue], [THEME_TOKENS.base.color.action, ADVISOR_CONSOLE_COLORS.cyan], [THEME_TOKENS.base.color.dim1, ADVISOR_CONSOLE_COLORS.lightBlueMist], [THEME_TOKENS.base.color.success, ADVISOR_CONSOLE_COLORS.success], [THEME_TOKENS.base.color.successBackground, ADVISOR_CONSOLE_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, ADVISOR_CONSOLE_COLORS.successBorder], [THEME_TOKENS.base.color.successText, ADVISOR_CONSOLE_COLORS.successText], [THEME_TOKENS.base.color.warning, ADVISOR_CONSOLE_COLORS.warning], [THEME_TOKENS.base.color.warningBackground, ADVISOR_CONSOLE_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, ADVISOR_CONSOLE_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, ADVISOR_CONSOLE_COLORS.warningText], [THEME_TOKENS.base.color.danger, ADVISOR_CONSOLE_COLORS.danger], [THEME_TOKENS.base.color.dangerBackground, ADVISOR_CONSOLE_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, ADVISOR_CONSOLE_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, ADVISOR_CONSOLE_COLORS.dangerText], [THEME_TOKENS.base.color.info, ADVISOR_CONSOLE_COLORS.blue], [THEME_TOKENS.base.color.infoBackground, ADVISOR_CONSOLE_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, ADVISOR_CONSOLE_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, ADVISOR_CONSOLE_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, ADVISOR_CONSOLE_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, ADVISOR_CONSOLE_COLORS.warningBackgroundBadge]], false))
});
var ABACUS_COLORS = {
  lightGrey: '#f5f5f5',
  lightColor: '#f4f9fd',
  blueJeans: '#12a9fb',
  // nav
  medBlue: '#048CD6',
  darkBlue: '#2C3F5E',
  skyPop: '#76CBF9',
  // nav
  // BADGE / NOTICE
  infoDark: '#424243',
  infoBackground: '#f2f2f2',
  infoBorder: '#b7b7b8',
  infoText: '#1c1c1c'
};
({
  themeSystemName: 'abacus',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'AbacusInterstitial'], [THEME_CONFIG.navigation.id.logo, 'AbacusNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, ABACUS_COLORS.darkBlue], [THEME_TOKENS.base.color.secondaryText, ABACUS_COLORS.darkBlue], [THEME_TOKENS.base.color.action, ABACUS_COLORS.medBlue], [THEME_TOKENS.base.color.loadingBar, ABACUS_COLORS.medBlue], [THEME_TOKENS.base.color.dim1, ABACUS_COLORS.lightColor],
  // success, warning, danger are inherited from root
  [THEME_TOKENS.base.color.info, ABACUS_COLORS.infoDark], [THEME_TOKENS.base.color.infoBackground, ABACUS_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, ABACUS_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, ABACUS_COLORS.infoText],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, ABACUS_COLORS.skyPop], [THEME_TOKENS.navigation.color.background, ABACUS_COLORS.darkBlue]], false))
});
var CAVA_COLORS = {
  gray100: 'rgba(25, 25, 15, 0.05)',
  gray300: 'rgba(25, 25, 15, 0.1)',
  // neutral
  neutral0: '#FFFFFF',
  neutral100: '#FAFAF5',
  neutral200: '#F5F5EB',
  // off white
  neutral500: '#D2D2C8',
  neutral600: '#919183',
  neutral700: '#838377',
  neutral800: '#6E6E64',
  neutral900: '#44443C',
  neutral950: '#14140F',
  // off black
  // Red
  red100: '#FDE7ED',
  red600: '#D20F46',
  // Orange
  orange500: '#FF8C5F',
  // amber
  amber100: '#FFEDB8',
  amber400: '#FFBE00',
  amber700: '#805F00',
  amber900: '#5C4400',
  // lime´
  lime400: '#BEFF50',
  // perk
  // green
  green200: '#D6FFEF',
  green800: '#007D4B',
  // emerald
  emerald50: '#EDFFFD',
  emerald800: '#06806E',
  // cyan
  cyan400: '#82DCFA',
  // blue
  blue50: '#F0F5FF',
  blue900: '#002971',
  // purple
  purple50: '#F1F0FF',
  purple300: '#C8A0FF',
  purple700: '#5647B2',
  // pink
  pink300: '#FAB9FA'
};
({
  themeSystemName: 'cava',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.flag.isFlagshipSite, true], [THEME_CONFIG.base.id.logoInterstitial, 'CavaInterstitial'], [THEME_CONFIG.navigation.id.logo, 'CavaNavigation']], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // avatar
  [THEME_TOKENS.avatar.color.background1, CAVA_COLORS.orange500], [THEME_TOKENS.avatar.color.background2, CAVA_COLORS.cyan400], [THEME_TOKENS.avatar.color.background3, CAVA_COLORS.pink300], [THEME_TOKENS.avatar.color.background4, CAVA_COLORS.purple300], [THEME_TOKENS.avatar.color.border, 'transparent'],
  // base
  [THEME_TOKENS.base.color.primaryText, CAVA_COLORS.neutral950], [THEME_TOKENS.base.color.secondaryText, CAVA_COLORS.neutral800], [THEME_TOKENS.base.color.action, CAVA_COLORS.neutral950], [THEME_TOKENS.base.color.siteBackground, CAVA_COLORS.neutral0], [THEME_TOKENS.base.color.sidebarBackground, 'transparent'], [THEME_TOKENS.base.color.loaderStart, CAVA_COLORS.gray100], [THEME_TOKENS.base.color.loaderEnd, CAVA_COLORS.gray300], [THEME_TOKENS.base.color.loadingBar, CAVA_COLORS.lime400], [THEME_TOKENS.base.color.dim1, CAVA_COLORS.neutral100],
  // neutral is inherited from root
  // success
  [THEME_TOKENS.base.color.success, CAVA_COLORS.green800], [THEME_TOKENS.base.color.successBackground, CAVA_COLORS.green200], [THEME_TOKENS.base.color.successBorder, CAVA_COLORS.green800], [THEME_TOKENS.base.color.successText, CAVA_COLORS.green800],
  // warning & warningBackground are inherited from root
  [THEME_TOKENS.base.color.warningBorder, getTokenValue(THEME_TOKENS.base.color.warning)], [THEME_TOKENS.base.color.warningText, getTokenValue(THEME_TOKENS.base.color.warning)],
  // danger
  [THEME_TOKENS.base.color.danger, CAVA_COLORS.red600], [THEME_TOKENS.base.color.dangerBackground, CAVA_COLORS.red100], [THEME_TOKENS.base.color.dangerBorder, CAVA_COLORS.red600], [THEME_TOKENS.base.color.dangerText, CAVA_COLORS.red600],
  // info
  [THEME_TOKENS.base.color.info, CAVA_COLORS.neutral500], [THEME_TOKENS.base.color.infoBackground, CAVA_COLORS.neutral100], [THEME_TOKENS.base.color.infoBorder, CAVA_COLORS.neutral500], [THEME_TOKENS.base.color.infoText, CAVA_COLORS.neutral800], [THEME_TOKENS.base.size.rootText, '16px'], [THEME_TOKENS.base.size.header1, '33px'], [THEME_TOKENS.base.size.header2, '23px'], [THEME_TOKENS.base.size.header3, '19px'], [THEME_TOKENS.base.size.header4, '16px'], [THEME_TOKENS.base.size.formInputText, '16px'], [THEME_TOKENS.base.fontFamily.body, '"PerkSans", Arial, sans-serif'], [THEME_TOKENS.base.fontFamily.heading, getTokenValue(THEME_TOKENS.base.fontFamily.body)], [THEME_TOKENS.base.fontWeight.bold, '600'],
  // badge
  // info
  [THEME_TOKENS.badge.color.infoBackground, CAVA_COLORS.neutral200], [THEME_TOKENS.badge.color.infoText, CAVA_COLORS.neutral800],
  // warning
  [THEME_TOKENS.badge.color.warningText, CAVA_COLORS.amber700],
  // button
  [THEME_TOKENS.button.color.disabledText, CAVA_COLORS.neutral700],
  // card
  [THEME_TOKENS.card.color.border, CAVA_COLORS.neutral500], [THEME_TOKENS.card.color.background, CAVA_COLORS.neutral100], [THEME_TOKENS.card.color.backgroundSecondary, CAVA_COLORS.neutral200],
  // notice
  [THEME_TOKENS.notice.color.successBackground, getTokenValue(THEME_TOKENS.base.color.successBackground)], [THEME_TOKENS.notice.color.successBorder, getTokenValue(THEME_TOKENS.base.color.successBorder)], [THEME_TOKENS.notice.color.successText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.notice.color.warningBackground, getTokenValue(THEME_TOKENS.base.color.warningBackground)], [THEME_TOKENS.notice.color.warningBorder, getTokenValue(THEME_TOKENS.base.color.warningBorder)], [THEME_TOKENS.notice.color.warningText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.notice.color.dangerBackground, getTokenValue(THEME_TOKENS.base.color.dangerBackground)], [THEME_TOKENS.notice.color.dangerBorder, getTokenValue(THEME_TOKENS.base.color.dangerBorder)], [THEME_TOKENS.notice.color.dangerText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.notice.color.infoBackground, getTokenValue(THEME_TOKENS.base.color.infoBackground)], [THEME_TOKENS.notice.color.infoBorder, getTokenValue(THEME_TOKENS.base.color.infoBorder)], [THEME_TOKENS.notice.color.infoText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.notice.size.fontWeight, getTokenValue(THEME_TOKENS.base.fontWeight.normal)],
  // flightOptions
  [THEME_TOKENS.flightOptions.color.airlineCardAirlineName, getTokenValue(THEME_TOKENS.base.color.secondaryText)],
  // textInput.color
  [THEME_TOKENS.textInput.color.border, CAVA_COLORS.neutral600], [THEME_TOKENS.textInput.color.backgroundHover, CAVA_COLORS.gray100], [THEME_TOKENS.textInput.color.placeholderText, getTokenValue(THEME_TOKENS.base.color.secondaryText)], [THEME_TOKENS.textInput.color.disabledText, CAVA_COLORS.neutral700],
  // textInput.shadow
  [THEME_TOKENS.textInput.shadow.focus, "0px 0px 0px 1px ".concat(getTokenValue(THEME_TOKENS.base.color.action))],
  // textInput.size
  [THEME_TOKENS.textInput.size.borderRadius, SPACE.size8],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.navigation.color.link, CAVA_COLORS.neutral800], [THEME_TOKENS.navigation.color.linkSelected, getTokenValue(THEME_TOKENS.base.color.primaryText)], [THEME_TOKENS.navigation.color.background, CAVA_COLORS.neutral100], [THEME_TOKENS.navigation.color.sectionBackground, CAVA_COLORS.neutral200], [THEME_TOKENS.navigation.color.footerBackground, CAVA_COLORS.neutral0], [THEME_TOKENS.navigation.color.helpCenterBackground, CAVA_COLORS.neutral950], [THEME_TOKENS.navigation.color.hamburgerMobile, CAVA_COLORS.neutral950], [THEME_TOKENS.navigation.color.border, CAVA_COLORS.neutral500]], false))
});
var spendColorsObj = {
  // INFO (modified with tinycolor)
  info1: '#0D74CE',
  // passport11
  info2: '#e7f1fa',
  info3: '#a3caec',
  info4: '#053157',
  // CORE (colors for multiple Emburse themes)
  checkpoint11_2: '#a12226',
  checkpoint11: '#CE2C31',
  checkpoint12: '#641723',
  checkpoint3: '#FEEBEC',
  checkpoint5: '#FFCDCE',
  departure11_2: '#1a6645',
  departure11: '#218358',
  departure12: '#193B2D',
  departure3: '#E6F6EB',
  departure5: '#C4E8D1',
  passport11_2: '#0A5AA1',
  passport11: '#0D74CE',
  passport12: '#113264',
  passport3: '#E6F4FE',
  passport5: '#C2E5FF',
  runway0: '#FFFFFF',
  runway1: '#FCFCFD',
  runway11: '#60646C',
  runway12: '#1C2024',
  runway2: '#F9F9FB',
  runway4: '#E8E8EC',
  runway6: '#D9D9E0',
  runway8: '#B9BBC6',
  winsock10: '#EF5F00',
  winsock12: '#582D1D',
  winsock3: '#FFEFD6',
  winsock5: '#FFD19A'
};
var EMBURSE_COLORS = __assign({
  // PAGE
  pagePrimary: spendColorsObj.runway1,
  pageSecondary: spendColorsObj.runway2,
  pageWhite: spendColorsObj.runway0,
  // SURFACE
  surfacePrimary: spendColorsObj.runway0,
  surfacePrimaryHover: spendColorsObj.passport3,
  surfacePrimarySelected: spendColorsObj.passport5,
  surfaceAccent: spendColorsObj.passport3,
  surfaceWarning: spendColorsObj.winsock3,
  surfaceError: spendColorsObj.checkpoint3,
  surfaceSuccess: spendColorsObj.departure3,
  surfaceNeutral: spendColorsObj.runway4,
  // BORDER
  borderPrimary: spendColorsObj.runway4,
  borderSecondary: spendColorsObj.runway6,
  borderAccent: spendColorsObj.passport5,
  borderWarning: spendColorsObj.winsock5,
  borderError: spendColorsObj.checkpoint5,
  borderSuccess: spendColorsObj.departure5,
  // TEXT
  textPrimary: spendColorsObj.runway12,
  textSecondary: spendColorsObj.runway11,
  textLink: spendColorsObj.passport11,
  textLinkHover: spendColorsObj.passport11_2,
  textError: spendColorsObj.checkpoint11,
  textDisabled: spendColorsObj.runway8,
  textReverse: spendColorsObj.runway0,
  // NOTIFICATION
  notificationDefault: spendColorsObj.runway4,
  notificationAccent: spendColorsObj.passport11,
  notificationWarning: spendColorsObj.winsock10,
  notificationUrgent: spendColorsObj.checkpoint11,
  notificationConfirm: spendColorsObj.departure11,
  // BUTTON
  buttonPrimary: spendColorsObj.passport11,
  buttonPrimaryHover: spendColorsObj.passport11_2,
  buttonPrimaryActive: spendColorsObj.passport12,
  buttonPrimaryText: spendColorsObj.runway0,
  buttonSecondary: spendColorsObj.runway0,
  buttonSecondaryText: spendColorsObj.passport11,
  buttonSecondaryStroke: spendColorsObj.passport11,
  buttonSecondaryHover: spendColorsObj.passport3,
  buttonSecondaryActive: spendColorsObj.passport5,
  buttonDisabled: spendColorsObj.runway6,
  // BUTTON EXTRA
  buttonExtraDanger: spendColorsObj.checkpoint11,
  buttonExtraDangerHover: spendColorsObj.checkpoint11_2,
  buttonExtraDangerActive: spendColorsObj.checkpoint12,
  buttonExtraSuccess: spendColorsObj.departure11,
  buttonExtraSuccessHover: spendColorsObj.departure11_2,
  buttonExtraSuccessActive: spendColorsObj.departure12,
  // ICON
  iconRegular: spendColorsObj.runway12,
  iconDisabled: spendColorsObj.runway8,
  iconAction: spendColorsObj.passport11,
  iconWarning: spendColorsObj.winsock10,
  iconError: spendColorsObj.checkpoint11,
  iconSuccess: spendColorsObj.departure11,
  iconReverse: spendColorsObj.runway0,
  // ALERT
  alertAccent: spendColorsObj.passport3,
  alertAccentText: spendColorsObj.passport12,
  alertWarning: spendColorsObj.winsock3,
  alertWarningText: spendColorsObj.winsock12,
  alertError: spendColorsObj.checkpoint3,
  alertErrorText: spendColorsObj.checkpoint12,
  alertSuccess: spendColorsObj.departure3,
  alertSuccessText: spendColorsObj.departure12
}, spendColorsObj);
var emburseTheme = {
  themeSystemName: 'emburse',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, EMBURSE_COLORS.textPrimary], [THEME_TOKENS.base.color.secondaryText, EMBURSE_COLORS.textSecondary], [THEME_TOKENS.base.color.action, EMBURSE_COLORS.passport11], [THEME_TOKENS.base.color.siteBackground, EMBURSE_COLORS.pagePrimary], [THEME_TOKENS.base.color.loadingBar, EMBURSE_COLORS.passport11],
  // dim1 is inherited from root
  [THEME_TOKENS.base.color.dim2, EMBURSE_COLORS.surfaceAccent], [THEME_TOKENS.base.color.disabled, EMBURSE_COLORS.textDisabled], [THEME_TOKENS.base.color.success, EMBURSE_COLORS.notificationConfirm], [THEME_TOKENS.base.color.successBackground, EMBURSE_COLORS.alertSuccess], [THEME_TOKENS.base.color.successBorder, EMBURSE_COLORS.borderSuccess], [THEME_TOKENS.base.color.successText, EMBURSE_COLORS.alertSuccessText], [THEME_TOKENS.base.color.warning, EMBURSE_COLORS.notificationWarning], [THEME_TOKENS.base.color.warningBackground, EMBURSE_COLORS.alertWarning], [THEME_TOKENS.base.color.warningBorder, EMBURSE_COLORS.borderWarning], [THEME_TOKENS.base.color.warningText, EMBURSE_COLORS.alertWarningText], [THEME_TOKENS.base.color.danger, EMBURSE_COLORS.notificationUrgent], [THEME_TOKENS.base.color.dangerBackground, EMBURSE_COLORS.alertError], [THEME_TOKENS.base.color.dangerBorder, EMBURSE_COLORS.borderError], [THEME_TOKENS.base.color.dangerText, EMBURSE_COLORS.alertErrorText], [THEME_TOKENS.base.color.info, EMBURSE_COLORS.notificationAccent], [THEME_TOKENS.base.color.infoBackground, EMBURSE_COLORS.alertAccent], [THEME_TOKENS.base.color.infoBorder, EMBURSE_COLORS.borderAccent], [THEME_TOKENS.base.color.infoText, EMBURSE_COLORS.alertAccentText], [THEME_TOKENS.card.color.border, EMBURSE_COLORS.borderPrimary],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, EMBURSE_COLORS.info3], [THEME_TOKENS.navigation.color.background, EMBURSE_COLORS.passport12]], false))
};
({
  themeSystemName: 'certify',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'CertifyInterstitial'], [THEME_CONFIG.navigation.id.logo, 'CertifyNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray([], __read(emburseTheme.themeSystemCssPropertiesMap), false))
});
({
  themeSystemName: 'chromeriver',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'ChromeRiverInterstitial'], [THEME_CONFIG.navigation.id.logo, 'ChromeRiverNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray([], __read(emburseTheme.themeSystemCssPropertiesMap), false))
});
var EA_COLORS = {
  lightBlue: '#95c4ff',
  // nav
  sapphireBlue: '#0E51A8',
  polynesianBlue: '#0c4388',
  pistachioGreen: '#8dc577',
  // nav
  // NOTICE
  asparagusGreen: '#5fa444',
  // success
  successBackground: '#eff6ec',
  successBorder: '#c2dcb8',
  successText: '#28451d',
  orange: '#e87e04',
  // warning
  warningBackground: '#fdf2e6',
  warningBorder: '#f6cea0',
  warningText: '#613502',
  red: '#D64541',
  // danger
  dangerBackground: '#fbecec',
  dangerBorder: '#efb8b7',
  dangerText: '#5a1d1b',
  infoBlue: '#156da8',
  // nav
  infoBackground: '#e8f0f6',
  infoBorder: '#a6c8de',
  infoText: '#092e47',
  // BADGE
  successBackgroundBadge: '#e1f7d9',
  warningBackgroundBadge: '#ffecd5'
};
({
  themeSystemName: 'ea',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'EAInterstitial'], [THEME_CONFIG.navigation.id.logo, 'EANavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, EA_COLORS.polynesianBlue], [THEME_TOKENS.base.color.action, EA_COLORS.sapphireBlue], [THEME_TOKENS.base.color.loadingBar, EA_COLORS.sapphireBlue],
  // dim colors are inherited from root
  [THEME_TOKENS.base.color.success, EA_COLORS.asparagusGreen], [THEME_TOKENS.base.color.successBackground, EA_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, EA_COLORS.successBorder], [THEME_TOKENS.base.color.successText, EA_COLORS.successText], [THEME_TOKENS.base.color.warning, EA_COLORS.orange], [THEME_TOKENS.base.color.warningBackground, EA_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, EA_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, EA_COLORS.warningText], [THEME_TOKENS.base.color.danger, EA_COLORS.red], [THEME_TOKENS.base.color.dangerBackground, EA_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, EA_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, EA_COLORS.dangerText], [THEME_TOKENS.base.color.info, EA_COLORS.infoBlue], [THEME_TOKENS.base.color.infoBackground, EA_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, EA_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, EA_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, EA_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, EA_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.background, EA_COLORS.sapphireBlue], [THEME_TOKENS.navigation.color.border, EA_COLORS.infoBlue]], false))
});
var EDENRED_PAY_COLORS = {
  white: '#FFFFFF',
  lightGrey: '#e9edf3',
  // nav
  grey: '#6D7787',
  black: '#000000',
  // nav
  coldWhite: '#F1F7FF',
  accentCobalt: '#dff4f9',
  lightCobalt: '#B0EFFF',
  // nav
  cobalt: '#0D8AFF',
  // nav
  darkBlue: '#22222d',
  // nav
  red: '#F72717',
  // nav
  violet: '#711CFF',
  mint: '#00A184',
  // NOTICE
  olive: '#81C105',
  // success
  successBackground: '#f2f9e6',
  successBorder: '#cfe7a0',
  successText: '#365102',
  orange: '#f79517',
  // warning
  warningBackground: '#fef4e8',
  warningBorder: '#fcd7a7',
  warningText: '#683f0a',
  vibrantRed: '#FF007D',
  // danger
  dangerBackground: '#ffe6f2',
  dangerBorder: '#ff9ece',
  dangerText: '#6b0035',
  infoCobalt: '#00529f',
  infoBackground: '#e6eef5',
  infoBorder: '#9ebddb',
  infoText: '#002243',
  // BADGE
  successBackgroundBadge: '#e8f6ce',
  warningBackgroundBadge: '#ffefda'
};
({
  themeSystemName: 'edenredpay',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'EdenredPayInterstitial'], [THEME_CONFIG.navigation.id.logo, 'EdenredPayNavigation'],
  // edenredpay is the only white label theme that has a light navigation background color
  // which does not work with the current "powered by AmTrav" logo with white text
  [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, false]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, EDENRED_PAY_COLORS.black], [THEME_TOKENS.base.color.secondaryText, EDENRED_PAY_COLORS.black], [THEME_TOKENS.base.color.action, EDENRED_PAY_COLORS.cobalt], [THEME_TOKENS.base.color.loadingBar, EDENRED_PAY_COLORS.cobalt], [THEME_TOKENS.base.color.dim1, EDENRED_PAY_COLORS.coldWhite], [THEME_TOKENS.base.color.dim2, EDENRED_PAY_COLORS.accentCobalt], [THEME_TOKENS.base.color.success, EDENRED_PAY_COLORS.olive], [THEME_TOKENS.base.color.successBackground, EDENRED_PAY_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, EDENRED_PAY_COLORS.successBorder], [THEME_TOKENS.base.color.successText, EDENRED_PAY_COLORS.successText], [THEME_TOKENS.base.color.warning, EDENRED_PAY_COLORS.orange], [THEME_TOKENS.base.color.warningBackground, EDENRED_PAY_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, EDENRED_PAY_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, EDENRED_PAY_COLORS.warningText], [THEME_TOKENS.base.color.danger, EDENRED_PAY_COLORS.vibrantRed], [THEME_TOKENS.base.color.dangerBackground, EDENRED_PAY_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, EDENRED_PAY_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, EDENRED_PAY_COLORS.dangerText], [THEME_TOKENS.base.color.info, EDENRED_PAY_COLORS.infoCobalt], [THEME_TOKENS.base.color.infoBackground, EDENRED_PAY_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, EDENRED_PAY_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, EDENRED_PAY_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, EDENRED_PAY_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, EDENRED_PAY_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, EDENRED_PAY_COLORS.red], [THEME_TOKENS.navigation.color.link, EDENRED_PAY_COLORS.black], [THEME_TOKENS.navigation.color.background, EDENRED_PAY_COLORS.white], [THEME_TOKENS.navigation.color.linkSelected, EDENRED_PAY_COLORS.black], [THEME_TOKENS.navigation.color.sectionBackground, EDENRED_PAY_COLORS.coldWhite], [THEME_TOKENS.navigation.color.border, EDENRED_PAY_COLORS.lightGrey]], false))
});
var LASSO_COLORS = {
  black: '#000000',
  darkGrey: '#262626',
  // nav
  blue: '#008aab',
  yellow: '#f5bf3b',
  // nav
  goldenSand: '#EDD391',
  // nav
  charcoalNavBorder: '#414141',
  // nav
  // NOTICE
  green: '#5fa444',
  // success
  successBackground: '#eff6ec',
  successBorder: '#c2dcb8',
  successText: '#28451d',
  orange: '#e87e04',
  // warning
  warningBackground: '#fdf2e6',
  warningBorder: '#f6cea0',
  warningText: '#613502',
  red: '#D64541',
  // danger
  dangerBackground: '#fbecec',
  dangerBorder: '#efb8b7',
  dangerText: '#5a1d1b',
  infoBlue: '#156da8',
  infoBackground: '#e8f0f6',
  infoBorder: '#a6c8de',
  infoText: '#092e47',
  // BADGE
  successBackgroundBadge: '#e1f7d9',
  warningBackgroundBadge: '#ffecd5'
};
({
  themeSystemName: 'lasso',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'LassoInterstitial'], [THEME_CONFIG.navigation.id.logo, 'LassoNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, LASSO_COLORS.black], [THEME_TOKENS.base.color.action, LASSO_COLORS.blue],
  // dim colors are inherited from root
  [THEME_TOKENS.base.color.success, LASSO_COLORS.green], [THEME_TOKENS.base.color.successBackground, LASSO_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, LASSO_COLORS.successBorder], [THEME_TOKENS.base.color.successText, LASSO_COLORS.successText], [THEME_TOKENS.base.color.warning, LASSO_COLORS.orange], [THEME_TOKENS.base.color.warningBackground, LASSO_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, LASSO_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, LASSO_COLORS.warningText], [THEME_TOKENS.base.color.danger, LASSO_COLORS.red], [THEME_TOKENS.base.color.dangerBackground, LASSO_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, LASSO_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, LASSO_COLORS.dangerText], [THEME_TOKENS.base.color.info, LASSO_COLORS.infoBlue], [THEME_TOKENS.base.color.infoBackground, LASSO_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, LASSO_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, LASSO_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, LASSO_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, LASSO_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, LASSO_COLORS.goldenSand], [THEME_TOKENS.navigation.color.background, LASSO_COLORS.black], [THEME_TOKENS.navigation.color.border, LASSO_COLORS.charcoalNavBorder]], false))
});

// CERTIFY - CHROME RIVER - EMBURSE
var NEXONIA_COLORS = {
  // link
  azureBlue: '#2681DD',
  yaleBlue: '#154E88',
  // active
  // navigation
  sapphireBlue: '#005EB8',
  blueNavBorder: '#4a84bb',
  // nav
  // subhead
  carolinaBlue: '#0097DC',
  // secondary
  culturedWhite: '#F3F4F5',
  platinum: '#E5E7E9',
  // hover
  gainsboro: '#D7DBDE',
  // active
  // primary
  lightGreen: '#6fe593',
  // nav
  pantoneGreen: '#1FB64D',
  forestGreenWeb: '#188A3B',
  // hover
  cadmiumGreen: '#105F28',
  // active
  // NOTICE
  malachiteGreen: '#23D158',
  // success
  successBackground: '#e9faee',
  successBorder: '#abeec0',
  successText: '#0f5825',
  greenBlue: '#1C68B4',
  // hover & info
  infoBackground: '#e8f0f8',
  infoBorder: '#a9c6e3',
  infoText: '#0c2c4c',
  // BADGE
  successBackgroundBadge: '#def6e5'
};
({
  themeSystemName: 'nexonia',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'NexoniaInterstitial'], [THEME_CONFIG.navigation.id.logo, 'NexoniaNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // primaryText & secondaryText are inherited from root
  [THEME_TOKENS.base.color.action, NEXONIA_COLORS.azureBlue], [THEME_TOKENS.base.color.loadingBar, NEXONIA_COLORS.pantoneGreen], [THEME_TOKENS.base.color.dim1, NEXONIA_COLORS.culturedWhite],
  // warning, danger are inherited from root
  [THEME_TOKENS.base.color.success, NEXONIA_COLORS.malachiteGreen], [THEME_TOKENS.base.color.successBackground, NEXONIA_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, NEXONIA_COLORS.successBorder], [THEME_TOKENS.base.color.successText, NEXONIA_COLORS.successText], [THEME_TOKENS.base.color.info, NEXONIA_COLORS.greenBlue], [THEME_TOKENS.base.color.infoBackground, NEXONIA_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, NEXONIA_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, NEXONIA_COLORS.infoText],
  // badge
  [THEME_TOKENS.badge.color.successBackground, NEXONIA_COLORS.successBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.background, NEXONIA_COLORS.sapphireBlue], [THEME_TOKENS.navigation.color.border, NEXONIA_COLORS.blueNavBorder]], false))
});
({
  themeSystemName: 'spend',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'SpendInterstitial'], [THEME_CONFIG.navigation.id.logo, 'SpendNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray([], __read(emburseTheme.themeSystemCssPropertiesMap), false))
});
var SUTISOFT_COLORS = {
  lightBlueJeans: '#41b0f7',
  // nav
  blue: '#0081c0',
  darkBlue: '#00496f',
  softAzure: '#82C1E9',
  // nav
  blueNavBorder: '#236283',
  // nav
  // NOTICE
  green: '#5fa444',
  // success
  successBackground: '#eff6ec',
  successBorder: '#c2dcb8',
  successText: '#28451d',
  orange: '#e87e04',
  // warning
  warningBackground: '#fdf2e6',
  warningBorder: '#f6cea0',
  warningText: '#613502',
  red: '#D64541',
  // danger
  dangerBackground: '#fbecec',
  dangerBorder: '#efb8b7',
  dangerText: '#5a1d1b',
  infoBlue: '#156da8',
  infoBackground: '#e8f0f6',
  infoBorder: '#a6c8de',
  infoText: '#092e47',
  // BADGE
  successBackgroundBadge: '#e1f7d9',
  warningBackgroundBadge: '#ffecd5'
};
({
  themeSystemName: 'sutisoft',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'SutiSoftInterstitial'], [THEME_CONFIG.navigation.id.logo, 'SutiSoftNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [[THEME_TOKENS.base.color.primaryText, SUTISOFT_COLORS.darkBlue], [THEME_TOKENS.base.color.action, SUTISOFT_COLORS.blue], [THEME_TOKENS.base.color.loadingBar, SUTISOFT_COLORS.blue],
  // dim colors are inherited from root
  [THEME_TOKENS.base.color.success, SUTISOFT_COLORS.green], [THEME_TOKENS.base.color.successBackground, SUTISOFT_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, SUTISOFT_COLORS.successBorder], [THEME_TOKENS.base.color.successText, SUTISOFT_COLORS.successText], [THEME_TOKENS.base.color.warning, SUTISOFT_COLORS.orange], [THEME_TOKENS.base.color.warningBackground, SUTISOFT_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, SUTISOFT_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, SUTISOFT_COLORS.warningText], [THEME_TOKENS.base.color.danger, SUTISOFT_COLORS.red], [THEME_TOKENS.base.color.dangerBackground, SUTISOFT_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, SUTISOFT_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, SUTISOFT_COLORS.dangerText], [THEME_TOKENS.base.color.info, SUTISOFT_COLORS.infoBlue], [THEME_TOKENS.base.color.infoBackground, SUTISOFT_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, SUTISOFT_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, SUTISOFT_COLORS.infoText],
  // badge.color
  [THEME_TOKENS.badge.color.successBackground, SUTISOFT_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, SUTISOFT_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, SUTISOFT_COLORS.softAzure], [THEME_TOKENS.navigation.color.background, SUTISOFT_COLORS.darkBlue], [THEME_TOKENS.navigation.color.border, SUTISOFT_COLORS.blueNavBorder]], false))
});
var SVB_COLORS = {
  bkgdGrey: '#F0F0F0',
  lightGrey: '#DCDCDC',
  lightBlueMist: '#f7fdff',
  // table
  cyanProcess: '#3ebded',
  lightBlue: '#33BBEF',
  // chat
  darkBlue: '#007DB8',
  prussianBlue: '#033149',
  crystalSurf: '#8FE0FF',
  // nav
  blueNavBorder: '#236283',
  // nav
  // NOTICE
  success: '#4BAF46',
  successBackground: '#edf7ed',
  successBorder: '#bbe1b9',
  successText: '#204a1d',
  warning: '#E77503',
  warningBackground: '#fdf1e6',
  warningBorder: '#f6cb9f',
  warningText: '#613101',
  danger: '#B00020',
  dangerBackground: '#f7e6e9',
  dangerBorder: '#e19eaa',
  dangerText: '#4a000d',
  deepBlue: '#004B6C',
  // info
  infoBackground: '#e6edf0',
  infoBorder: '#9ebbc7',
  infoText: '#00202d',
  // BADGE
  successBackgroundBadge: '#dffadf',
  warningBackgroundBadge: '#ffeddb'
};
({
  themeSystemName: 'svb',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'SVBInterstitial'], [THEME_CONFIG.navigation.id.logo, 'SVBNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // primaryText & secondaryText are inherited from root
  [THEME_TOKENS.base.color.action, SVB_COLORS.cyanProcess], [THEME_TOKENS.base.color.loadingBar, SVB_COLORS.darkBlue], [THEME_TOKENS.base.color.dim1, SVB_COLORS.lightBlueMist], [THEME_TOKENS.base.color.success, SVB_COLORS.success], [THEME_TOKENS.base.color.successBackground, SVB_COLORS.successBackground], [THEME_TOKENS.base.color.successBorder, SVB_COLORS.successBorder], [THEME_TOKENS.base.color.successText, SVB_COLORS.successText], [THEME_TOKENS.base.color.warning, SVB_COLORS.warning], [THEME_TOKENS.base.color.warningBackground, SVB_COLORS.warningBackground], [THEME_TOKENS.base.color.warningBorder, SVB_COLORS.warningBorder], [THEME_TOKENS.base.color.warningText, SVB_COLORS.warningText], [THEME_TOKENS.base.color.danger, SVB_COLORS.danger], [THEME_TOKENS.base.color.dangerBackground, SVB_COLORS.dangerBackground], [THEME_TOKENS.base.color.dangerBorder, SVB_COLORS.dangerBorder], [THEME_TOKENS.base.color.dangerText, SVB_COLORS.dangerText], [THEME_TOKENS.base.color.info, SVB_COLORS.deepBlue], [THEME_TOKENS.base.color.infoBackground, SVB_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, SVB_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, SVB_COLORS.infoText], [THEME_TOKENS.badge.color.successBackground, SVB_COLORS.successBackgroundBadge], [THEME_TOKENS.badge.color.warningBackground, SVB_COLORS.warningBackgroundBadge],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, SVB_COLORS.crystalSurf], [THEME_TOKENS.navigation.color.background, SVB_COLORS.deepBlue], [THEME_TOKENS.navigation.color.border, SVB_COLORS.blueNavBorder]], false))
});

// CERTIFY - CHROME RIVER - EMBURSE
var TALLIE_COLORS = {
  lightGrey: '#f5f5f5',
  lightColor: '#f5f9f1',
  // nav
  lightGreenTitle: '#ceff71',
  // nav
  lightGreen: '#7cbe00',
  green: '#6fa60f',
  darkGreen: '#406009',
  // BADGE / NOTICE
  infoDark: '#424241',
  infoBackground: '#f2f2f2',
  infoBorder: '#b7b7b7',
  infoText: '#1c1c1b'
};
({
  themeSystemName: 'tallie',
  themeSystemConfigMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemConfigMap), false), [[THEME_CONFIG.base.id.logoInterstitial, 'TallieInterstitial'], [THEME_CONFIG.navigation.id.logo, 'TallieNavigation'], [THEME_CONFIG.navigation.flag.showPoweredByAmtrav, true]], false)),
  themeSystemCssPropertiesMap: new Map(__spreadArray(__spreadArray([], __read(rootTheme.themeSystemCssPropertiesMap), false), [
  // primaryText & secondaryText is inherited from root
  [THEME_TOKENS.base.color.action, TALLIE_COLORS.green], [THEME_TOKENS.base.color.loadingBar, TALLIE_COLORS.green], [THEME_TOKENS.base.color.dim1, TALLIE_COLORS.lightColor],
  // success, warning, danger are inherited from root
  [THEME_TOKENS.base.color.info, TALLIE_COLORS.infoDark], [THEME_TOKENS.base.color.infoBackground, TALLIE_COLORS.infoBackground], [THEME_TOKENS.base.color.infoBorder, TALLIE_COLORS.infoBorder], [THEME_TOKENS.base.color.infoText, TALLIE_COLORS.infoText],
  // navigation
  [THEME_TOKENS.navigation.color.headingText, TALLIE_COLORS.lightGreenTitle], [THEME_TOKENS.navigation.color.background, TALLIE_COLORS.lightGreen]], false))
});
function generateCustomProperties(theme) {
  var styles = '';
  var themeSystemCssPropertiesMap = theme.themeSystemCssPropertiesMap;
  if (!themeSystemCssPropertiesMap) return styles;
  themeSystemCssPropertiesMap.forEach(function (value, key) {
    styles += "".concat(key, ": ").concat(value, ";");
  });
  return styles;
}
createGlobalStyle(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n\n  // CSS reset\n  // https://www.joshwcomeau.com/css/custom-css-reset/\n  *,\n  *::before,\n  *::after {\n    box-sizing: border-box;\n  }\n\n  * {\n    margin: 0;\n  }\n\n  html {\n    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);\n  }\n\n  body {\n    font-family: ", ";\n    font-size: ", ";\n    color: ", ";\n    -webkit-font-smoothing: antialiased;\n    -webkit-text-size-adjust: none;\n  }\n\n  p {\n    line-height: calc(1em + 0.5rem);\n    padding: 0;\n  }\n\n  b,\n  strong {\n    font-weight: bold;\n  }\n\n  a {\n    color: ", ";\n    text-decoration: underline;\n\n    &:hover,\n    &:focus,\n    &:active {\n      color: ", ";\n      text-decoration: none;\n    }\n\n    &:hover {\n      cursor: pointer;\n    }\n\n    &:focus {\n      outline: 0;\n    }\n  }\n\n  // Custom scrollbar\n\n\n  //media selector for mouse input devices\n  @media (pointer: fine) {\n    ::-webkit-scrollbar {\n        width: 12px;\n        height: 12px;\n    }\n\n    ::-webkit-scrollbar-track {\n        -webkit-box-shadow: inset 0 0 12px rgb(214, 214, 214);\n    }\n\n    ::-webkit-scrollbar-thumb {\n        background-color: #b5b5b5;\n        border: 2px solid #e7e7e7;\n        border-radius: 12px;\n    }\n\n    ::-webkit-scrollbar-thumb:hover {\n        background-color: #999999;\n    }\n  }\n"], ["\n\n  // CSS reset\n  // https://www.joshwcomeau.com/css/custom-css-reset/\n  *,\n  *::before,\n  *::after {\n    box-sizing: border-box;\n  }\n\n  * {\n    margin: 0;\n  }\n\n  html {\n    -webkit-tap-highlight-color: rgba(0, 0, 0, 0);\n  }\n\n  body {\n    font-family: ", ";\n    font-size: ", ";\n    color: ", ";\n    -webkit-font-smoothing: antialiased;\n    -webkit-text-size-adjust: none;\n  }\n\n  p {\n    line-height: calc(1em + 0.5rem);\n    padding: 0;\n  }\n\n  b,\n  strong {\n    font-weight: bold;\n  }\n\n  a {\n    color: ", ";\n    text-decoration: underline;\n\n    &:hover,\n    &:focus,\n    &:active {\n      color: ", ";\n      text-decoration: none;\n    }\n\n    &:hover {\n      cursor: pointer;\n    }\n\n    &:focus {\n      outline: 0;\n    }\n  }\n\n  // Custom scrollbar\n\n\n  //media selector for mouse input devices\n  @media (pointer: fine) {\n    ::-webkit-scrollbar {\n        width: 12px;\n        height: 12px;\n    }\n\n    ::-webkit-scrollbar-track {\n        -webkit-box-shadow: inset 0 0 12px rgb(214, 214, 214);\n    }\n\n    ::-webkit-scrollbar-thumb {\n        background-color: #b5b5b5;\n        border: 2px solid #e7e7e7;\n        border-radius: 12px;\n    }\n\n    ::-webkit-scrollbar-thumb:hover {\n        background-color: #999999;\n    }\n  }\n"])), getTokenValue(THEME_TOKENS.base.fontFamily.body), getTokenValue(THEME_TOKENS.base.size.bodyText), getTokenValue(THEME_TOKENS.base.color.secondaryText), getTokenValue(THEME_TOKENS.base.color.action), getTokenValue(THEME_TOKENS.base.color.action));
createGlobalStyle(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  :root { \n    ", "; \n  }\n"], ["\n  :root { \n    ", "; \n  }\n"])), function (_a) {
  var theme = _a.theme;
  return generateCustomProperties(theme);
});
var templateObject_1, templateObject_2;

const PriceContainer = styled(Flex)`
  flex-direction: column;
  margin-right: 2px;
  align-items: end;
`;
const PriceButtonWrapper = styled.div`
  min-height: 40px; // space for skeleton loader if no content is provided
  ${_ref => {
  let {
    isLoading
  } = _ref;
  return skeletonLoadingStyles(isLoading, true);
}}
`;
const TotalPrice = styled.div`
  font-size: 12px;
  padding-top: 2px;
  white-space: nowrap;
  color: ${getThemeProp('global.greyPalette.600')};
`;
const WithTaxes = styled.div`
  font-size: 10px;
  padding-top: 2px;
  color: ${getThemeProp('global.greyPalette.600')};
`;
const StyledActivityIndicator = styled(ActivityIndicator)`
  margin-right: 8px;
`;
const Price = _ref2 => {
  let {
    dailyRate,
    totalPrice,
    isLoading,
    onClick,
    priceIsSpinning
  } = _ref2;
  return /*#__PURE__*/React.createElement(PriceContainer, null, /*#__PURE__*/React.createElement(PriceButtonWrapper, {
    isLoading: isLoading
  }, !priceIsSpinning ? /*#__PURE__*/React.createElement(PriceButton, {
    price: dailyRate,
    onClick: onClick,
    label: "per day"
  }) : /*#__PURE__*/React.createElement(StyledActivityIndicator, null)), isLoading ? null : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(TotalPrice, null, `$${formatUSCurrencyNum(totalPrice)} total`), /*#__PURE__*/React.createElement(WithTaxes, null, "w/ taxes")));
};
Price.propTypes = {
  dailyRate: PropTypes.number,
  isLoading: PropTypes.bool,
  onClick: PropTypes.func,
  totalPrice: PropTypes.number,
  priceIsSpinning: PropTypes.bool
};

const CarOptionDetails = styled(Flex)`
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  padding: 12px 12px 12px 16px;
`;
const CarNamePriceRow = styled(Flex)`
  justify-content: space-between;
`;
const CarImageContainer = styled(Flex)`
  flex-direction: column;
  padding: 16px 12px 0px 12px;
  align-items: left;
  justify-content: space-between;
`;
styled(Flex)`
  width: 100%;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  padding-right: 12px;
`;
const OopPriceSection = styled(Flex)`
  min-width: 68px;
  justify-content: flex-end;
`;
const MetricTilesContainer = styled(Flex)`
  gap: 8px;
  padding-top: 8px;
  align-items: center;
`;
const DangerPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${getThemeProp('global.danger--color')};
  background-color: white;
  border-style: solid;
  border-width: 1px;
  border-color: ${getThemeProp('global.danger--color')};
  border-radius: 4px;
  padding: 2px 4px;
`;

const ManualTransmission = () => {
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(DangerPill, null, /*#__PURE__*/React.createElement(IconFlex, {
    inheritColor: true,
    size: 14
  }, /*#__PURE__*/React.createElement(IconGearShift, null)), "Manual Transmission"));
};

const SnowflakeOff = () => /*#__PURE__*/React.createElement("svg", {
  width: "14",
  height: "14",
  viewBox: "0 0 24 24",
  strokeWidth: "1.5",
  stroke: "currentColor",
  fill: "none",
  strokeLinecap: "round",
  strokeLinejoin: "round"
}, /*#__PURE__*/React.createElement("path", {
  stroke: "none",
  d: "M0 0h24v24H0z",
  fill: "none"
}), /*#__PURE__*/React.createElement("path", {
  d: "M10 4l2 1l2 -1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12 2v6m1.196 1.186l1.804 1.034"
}), /*#__PURE__*/React.createElement("path", {
  d: "M17.928 6.268l.134 2.232l1.866 1.232"
}), /*#__PURE__*/React.createElement("path", {
  d: "M20.66 7l-5.629 3.25l-.031 .75"
}), /*#__PURE__*/React.createElement("path", {
  d: "M19.928 14.268l-1.015 .67"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14.212 14.226l-2.171 1.262"
}), /*#__PURE__*/React.createElement("path", {
  d: "M14 20l-2 -1l-2 1"
}), /*#__PURE__*/React.createElement("path", {
  d: "M12 22v-6.5l-3 -1.72"
}), /*#__PURE__*/React.createElement("path", {
  d: "M6.072 17.732l-.134 -2.232l-1.866 -1.232"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3.34 17l5.629 -3.25l-.01 -3.458"
}), /*#__PURE__*/React.createElement("path", {
  d: "M4.072 9.732l1.866 -1.232l.134 -2.232"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3.34 7l5.629 3.25l.802 -.466"
}), /*#__PURE__*/React.createElement("path", {
  d: "M3 3l18 18"
}));
const NoAirConditioning = () => {
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(DangerPill, null, /*#__PURE__*/React.createElement(SnowflakeOff, null), "No Air Conditioning"));
};

const NameRatingContainer = styled(Box)`
  display: grid;
  min-height: 47px; // space for skeleton loader if no content is provided
  padding-bottom: 4px;
  ${_ref => {
  let {
    isLoading
  } = _ref;
  return skeletonLoadingStyles(isLoading);
}};
  max-width: 280px;
`;
const CarType = styled(Heading3)`
  margin-bottom: 4px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;
const MileageDisplay = styled.span`
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  padding: 10px 0px;

  ${props => props.deemphasize && css`
      color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
    `};
`;
const ManualNoAirCon = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;
const MakeModel = styled.div`
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
`;
const CarTypeMakeMiles = _ref2 => {
  let {
    carTypeDisplayName,
    makeModel,
    mileageDisplay,
    nameLoading,
    isManual,
    withAC,
    onNameClick,
    isRefundable
  } = _ref2;
  return /*#__PURE__*/React.createElement(NameRatingContainer, {
    isLoading: nameLoading
  }, /*#__PURE__*/React.createElement(CarType, {
    "data-click-target": "name",
    onClick: onNameClick,
    title: carTypeDisplayName
  }, carTypeDisplayName || "Supplier's Choice"), nameLoading ? null : /*#__PURE__*/React.createElement(MakeModel, null, makeModel || 'Vehicle assigned at pick-up'), /*#__PURE__*/React.createElement(MileageDisplay, {
    deemphasize: mileageDisplay === 'Unlimited Miles'
  }, mileageDisplay), /*#__PURE__*/React.createElement(ManualNoAirCon, null, isManual ? /*#__PURE__*/React.createElement(ManualTransmission, null) : null, !withAC ? /*#__PURE__*/React.createElement(NoAirConditioning, null) : null, /*#__PURE__*/React.createElement(RefundabilityBadge, {
    isRefundable: isRefundable
  })));
};
CarTypeMakeMiles.propTypes = {
  carTypeDisplayName: PropTypes.string,
  makeModel: PropTypes.string,
  mileageDisplay: PropTypes.string,
  nameLoading: PropTypes.bool,
  isManual: PropTypes.bool,
  withAC: PropTypes.bool,
  onNameClick: PropTypes.func,
  isRefundable: PropTypes.bool
};

const CarAddressContainer = styled(Flex)`
  align-items: center;
  gap: ${_ref => {
  let {
    extraSpacing
  } = _ref;
  return extraSpacing ? '6px 24px' : '6px';
}};
  flex-wrap: ${_ref2 => {
  let {
    extraSpacing
  } = _ref2;
  return extraSpacing ? 'wrap' : 'unset';
}};
  min-height: 18px; // space for skeleton loader if no content is provided
  margin-top: ${_ref3 => {
  let {
    isLoading
  } = _ref3;
  return isLoading ? '114px' : '16px';
}};
  ${_ref4 => {
  let {
    isLoading
  } = _ref4;
  return skeletonLoadingStyles(isLoading);
}};
  ${media.Medium`
    margin-top: ${_ref5 => {
  let {
    isLoading
  } = _ref5;
  return isLoading ? '80px' : '12px';
}};
  `};
`;
const GridAddressRegular = styled(AddressRegular)`
  display: grid;
`;
const TruncatedP = styled.p`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  margin: 0;
  line-height: inherit;
`;
const PickUpBadge = styled.div`
  display: inline-block;
  background: white;
  border-width: 0 0 0 2px;
  border-style: solid;
  border-color: ${getThemeProp('global.success--color')};
  background: ${getThemeProp('global.greyPalette.100')};
  padding: 1px 6px;
  margin-right: 2px;
  width: 64px;
  color: ${getThemeProp('global.textBody--color')};
`;
const DropOffBadge = styled(PickUpBadge)`
  border-color: ${getThemeProp('global.danger--color')};
`;
const CarAddressDisplay = _ref6 => {
  let {
    distance,
    addressDisplay,
    isLoading,
    dropoffAddress,
    compact
  } = _ref6;
  if (!addressDisplay && !dropoffAddress) return null;
  const milesAway = distance === null || distance === void 0 ? void 0 : distance.toFixed(1);
  const isDropoff = !!dropoffAddress;
  return /*#__PURE__*/React.createElement(CarAddressContainer, {
    isLoading: isLoading,
    extraSpacing: dropoffAddress
  }, !dropoffAddress ? /*#__PURE__*/React.createElement(Box, {
    flex: "0 0 auto"
  }, /*#__PURE__*/React.createElement(AreaDistance, {
    distance: milesAway
  })) : null, addressDisplay ? /*#__PURE__*/React.createElement(GridAddressRegular, {
    title: addressDisplay
  }, /*#__PURE__*/React.createElement(TruncatedP, {
    isDropoff: isDropoff,
    compact: compact
  }, !dropoffAddress ? addressDisplay : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PickUpBadge, null, "Pick Up"), " ", addressDisplay))) : null, dropoffAddress ? /*#__PURE__*/React.createElement(GridAddressRegular, {
    title: dropoffAddress
  }, /*#__PURE__*/React.createElement(TruncatedP, {
    isDropoff: isDropoff,
    compact: compact
  }, /*#__PURE__*/React.createElement(DropOffBadge, null, "Drop Off"), " ", dropoffAddress)) : null);
};
CarAddressDisplay.propTypes = {
  distance: PropTypes.number,
  addressDisplay: PropTypes.string,
  isLoading: PropTypes.bool,
  dropoffAddress: PropTypes.string,
  compact: PropTypes.bool
};

const BagSize = styled.span`
  font-size: 12px;
`;
const CarMetricTiles = _ref => {
  let {
    compact,
    numDoors,
    numPax,
    numBags = null,
    numBagsSmall = null
  } = _ref;
  let showBothBags = false;
  if (numBags && numBagsSmall) {
    showBothBags = true;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, numDoors ? /*#__PURE__*/React.createElement(MetricTileMini, {
    metric: numDoors,
    graphic: /*#__PURE__*/React.createElement(IconFlex, {
      inheritColor: true,
      size: compact ? 16 : 20
    }, /*#__PURE__*/React.createElement(IconCarDoor, null))
  }) : null, numPax ? /*#__PURE__*/React.createElement(MetricTileMini, {
    metric: numPax,
    graphic: /*#__PURE__*/React.createElement(IconFlex, {
      inheritColor: true,
      size: compact ? 16 : 20
    }, /*#__PURE__*/React.createElement(IconUser, null))
  }) : null, numBags ? /*#__PURE__*/React.createElement(MetricTileMini, {
    metric: /*#__PURE__*/React.createElement(Text, {
      size: 14
    }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("strong", null, numBags), " ", /*#__PURE__*/React.createElement(BagSize, null, "lg"), showBothBags ? /*#__PURE__*/React.createElement(React.Fragment, null, "\xA0+\xA0", /*#__PURE__*/React.createElement("strong", null, numBagsSmall), " ", /*#__PURE__*/React.createElement(BagSize, null, "sm")) : null)),
    graphic: /*#__PURE__*/React.createElement(IconFlex, {
      inheritColor: true,
      size: compact ? 16 : 20
    }, /*#__PURE__*/React.createElement(IconSuitcaseLine, null))
  }) : null);
};
CarMetricTiles.propTypes = {
  numBags: PropTypes.string,
  numBagsSmall: PropTypes.string,
  numPax: PropTypes.string,
  numDoors: PropTypes.string,
  compact: PropTypes.bool
};

const WheelDriveBadge = styled(Badge)`
  color: #573496;
  background-color: #dfc6ec;
`;
const ElectricBadge = () => {
  return /*#__PURE__*/React.createElement(Badge, {
    icon: /*#__PURE__*/React.createElement(IconPlug, null),
    noBorderRadius: true,
    kind: "primary",
    size: "small"
  }, "Electric");
};
const HybridBadge = () => {
  return /*#__PURE__*/React.createElement(Badge, {
    icon: /*#__PURE__*/React.createElement(IconEco, null),
    noBorderRadius: true,
    kind: "primary",
    size: "small"
  }, "Hybrid");
};
const OtherFuelBadge = _ref => {
  let {
    fuelLabel
  } = _ref;
  return /*#__PURE__*/React.createElement(Badge, {
    noBorderRadius: true,
    kind: "primary",
    size: "small"
  }, fuelLabel);
};
const AllWheelDriveBadge = () => {
  return /*#__PURE__*/React.createElement(WheelDriveBadge, {
    icon: /*#__PURE__*/React.createElement(IconCog, null),
    noBorderRadius: true,
    kind: "primary",
    size: "small"
  }, "All Wheel Drive");
};
const FourWheelDriveBadge = () => {
  return /*#__PURE__*/React.createElement(WheelDriveBadge, {
    icon: /*#__PURE__*/React.createElement(IconCog, null),
    noBorderRadius: true,
    kind: "primary",
    size: "small"
  }, "Four Wheel Drive");
};
OtherFuelBadge.propTypes = {
  fuelLabel: PropTypes.string.isRequired
};

const ShuttleBadge = styled.div`
  display: inline-block;
  white-space: nowrap;
  font-size: 14px;
  font-family: ${getThemeProp('global.textBody--fontFamily')};
  background-color: white;
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: 4px;
  padding: 6px 12px;
`;
const ShuttleInfo = styled.div`
  display: flex;
  font-weight: bold;
  gap: 8px;
  align-items: center;
  width: min-content;
  text-align: center;

  ${_ref => {
  let {
    withAddress
  } = _ref;
  return withAddress && css`
      padding-bottom: 4px;
      border-bottom: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
    `;
}};
`;
const Address = styled.div`
  margin-top: 4px;
  font-size: 12px;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 200px;
  overflow: hidden;
  color: ${getTokenValue(THEME_TOKENS.base.color.secondaryText)};
`;
const getTextFromCode = code => {
  switch (code) {
    case 'C':
      return 'Counter in terminal, shuttle to car';
    case 'S':
      return 'Shuttle to counter and car';
    case 'N':
      return 'Counter and car in terminal';
    case 'T':
      return 'Counter and car in terminal';
    default:
      return '';
  }
};
const AirportShuttle = _ref2 => {
  let {
    code,
    airportCode,
    address
  } = _ref2;
  return /*#__PURE__*/React.createElement(ShuttleBadge, null, /*#__PURE__*/React.createElement(ShuttleInfo, {
    withAddress: address
  }, /*#__PURE__*/React.createElement(Box, null, /*#__PURE__*/React.createElement("b", null, airportCode)), /*#__PURE__*/React.createElement(IconPlane, null), code ? /*#__PURE__*/React.createElement(Box, null, getTextFromCode(code)) : null), address ? /*#__PURE__*/React.createElement(Address, null, address) : null);
};
AirportShuttle.propTypes = {
  code: PropTypes.string,
  airportCode: PropTypes.string,
  address: PropTypes.string
};

const CarCard = styled(Flex)`
  flex-direction: column;
  width: 100%;
  background-color: white;
  color: ${getTokenValue(THEME_TOKENS.base.color.primaryText)};
  border: 1px solid ${getTokenValue(THEME_TOKENS.card.color.border)};
  border-radius: ${getTokenValue(THEME_TOKENS.card.size.borderRadiusLarge)};

  min-height: 140px;
  ${_ref => {
  let {
    active
  } = _ref;
  return active && css`
      outline-size: 4px;
      outline-style: solid;
      outline-offset: 0px;
      outline-color: ${getThemeProp('global.link--color')};
    `;
}};
`;
const CarCardWrapper = styled.div`
  @media (hover: hover) {
    &:hover ${CarCard} {
      ${props => !props.isLoading && css`
          cursor: pointer;
          background-color: ${lighten(getThemeProp('global.light--color')(props), 20)};
        `};
    }
  }
`;
const CarContent = styled(Flex)`
  flex-direction: row;
  height: unset;
  flex: 0 0 100%;
`;
const CarOptionCard = _ref2 => {
  let {
    carData = {},
    active = false,
    cardClicked,
    // triggers when most of the card is clicked
    pricesLoading = false,
    carsLoading = false,
    invitationToken = null,
    compact = false,
    showVendorLogo = true,
    showLocation = true,
    showShuttleInfo = false
  } = _ref2;
  const {
    pickUpAddressDisplay,
    carTypeDisplayName,
    carTypeCode,
    imageUrl,
    isRefundable,
    makeModel,
    mileageDisplay,
    automaticTransmission,
    withAC,
    distance,
    pickupShuttle,
    policyViolations,
    averageDailyRate,
    vendorCode,
    numPax,
    numDoors,
    numBags,
    numBagsSmall,
    pricingTotal,
    isPreferred,
    vehicleFuelType,
    vehicleDriveType,
    airportCode,
    dropoffAddress
  } = carData;
  const [priceSpinning, setPriceSpinning] = useState(false);
  const handleCardClick = e => {
    const hasTarget = e.target.closest('[data-click-target]');
    setPriceSpinning(true);
    if (!hasTarget) {
      cardClicked(() => setPriceSpinning(false));
    }
  };
  const displayShuttle = showShuttleInfo && airportCode && pickupShuttle;
  return /*#__PURE__*/React.createElement(CarCardWrapper, {
    isLoading: carsLoading || pricesLoading,
    compact: compact
  }, /*#__PURE__*/React.createElement(CarCard, {
    active: active,
    compact: compact,
    isLoading: carsLoading || pricesLoading
  }, !carsLoading ? /*#__PURE__*/React.createElement(Flex, {
    pt: SPACE.size8
  }, isPreferred ? /*#__PURE__*/React.createElement(PreferredBadge, null) : null, vehicleFuelType === 'Electric' ? /*#__PURE__*/React.createElement(ElectricBadge, null) : null, vehicleFuelType === 'Hybrid' ? /*#__PURE__*/React.createElement(HybridBadge, null) : null, vehicleFuelType && vehicleFuelType !== 'Hybrid' && vehicleFuelType !== 'Electric' && vehicleFuelType !== 'Gas' && vehicleFuelType !== 'Petrol' ? /*#__PURE__*/React.createElement(OtherFuelBadge, {
    fuelLabel: vehicleFuelType
  }) : null, vehicleDriveType === 'FourWD' ? /*#__PURE__*/React.createElement(FourWheelDriveBadge, null) : null, vehicleDriveType === 'AWD' ? /*#__PURE__*/React.createElement(AllWheelDriveBadge, null) : null) : null, /*#__PURE__*/React.createElement(CarContent, null, !compact && !carsLoading ? /*#__PURE__*/React.createElement(CarImageContainer, null, /*#__PURE__*/React.createElement(CarTypeImage, {
    makeModel: makeModel,
    imageUrl: imageUrl,
    code: carTypeCode,
    fuelType: vehicleFuelType,
    size: "large"
  }), showVendorLogo && /*#__PURE__*/React.createElement(CarVendorLogo, {
    size: "64px",
    constrain: "width",
    carCode: vendorCode
  })) : null, /*#__PURE__*/React.createElement(CarOptionDetails, {
    onClick: handleCardClick
  }, /*#__PURE__*/React.createElement(Flex, {
    justifyContent: "space-between"
  }, /*#__PURE__*/React.createElement(Box, {
    flex: "1 0 auto"
  }, compact && !carsLoading && showVendorLogo ? /*#__PURE__*/React.createElement(CarVendorLogo, {
    size: "16px",
    constrain: "height",
    carCode: vendorCode
  }) : null, /*#__PURE__*/React.createElement(CarNamePriceRow, null, /*#__PURE__*/React.createElement(CarTypeMakeMiles, {
    carTypeDisplayName: carTypeDisplayName,
    makeModel: makeModel,
    mileageDisplay: mileageDisplay,
    nameLoading: carsLoading,
    onNameClick: cardClicked,
    isManual: !automaticTransmission,
    withAC: withAC,
    isRefundable: isRefundable
  }))), /*#__PURE__*/React.createElement(OopPriceSection, null, priceSpinning ? null : /*#__PURE__*/React.createElement(OutOfPolicy, {
    mt: 1,
    mr: 1,
    policyViolations: policyViolations,
    isLoading: pricesLoading,
    invitationToken: invitationToken
  }), /*#__PURE__*/React.createElement(Price, {
    dailyRate: averageDailyRate,
    totalPrice: pricingTotal,
    isLoading: pricesLoading,
    priceIsSpinning: priceSpinning
  }))), carsLoading ? null : /*#__PURE__*/React.createElement(MetricTilesContainer, null, compact ? /*#__PURE__*/React.createElement(Box, {
    pr: 3
  }, /*#__PURE__*/React.createElement(CarTypeImage, {
    code: carTypeCode,
    size: "large"
  })) : null, /*#__PURE__*/React.createElement(CarMetricTiles, {
    numPax: numPax,
    numBags: numBags,
    numBagsSmall: numBagsSmall,
    numDoors: numDoors,
    compact: compact
  })), displayShuttle ? /*#__PURE__*/React.createElement(Box, {
    mt: 2
  }, /*#__PURE__*/React.createElement(AirportShuttle, {
    code: pickupShuttle,
    airportCode: airportCode,
    address: pickUpAddressDisplay
  })) : null, showLocation && /*#__PURE__*/React.createElement(CarAddressDisplay, {
    distance: distance,
    addressDisplay: displayShuttle ? null : pickUpAddressDisplay,
    isLoading: carsLoading,
    dropoffAddress: dropoffAddress === pickUpAddressDisplay ? null : dropoffAddress,
    compact: compact
  })))));
};
CarOptionCard.propTypes = {
  active: PropTypes.bool,
  /**
   * This function will execute when the card is clicked.
   * A function to stop the price from spinning will be passed into this function.
   */
  cardClicked: PropTypes.func,
  pricesLoading: PropTypes.bool,
  carsLoading: PropTypes.bool,
  invitationToken: PropTypes.string,
  compact: PropTypes.bool,
  showVendorLogo: PropTypes.bool,
  showLocation: PropTypes.bool,
  showShuttleInfo: PropTypes.bool,
  carData: PropTypes.shape({
    isManual: PropTypes.bool,
    averageDailyRate: PropTypes.number,
    carType: PropTypes.string,
    policyViolations: PropTypes.arrayOf(PropTypes.string),
    makeModel: PropTypes.string,
    mileageDisplay: PropTypes.string,
    numBags: PropTypes.string,
    numBagsSmall: PropTypes.string,
    numDoors: PropTypes.string,
    numPax: PropTypes.string,
    pickUpAddressDisplay: PropTypes.string,
    pricingTotal: PropTypes.number,
    quickBookEligible: PropTypes.bool,
    withAC: PropTypes.bool,
    distance: PropTypes.number,
    vendorCode: PropTypes.string,
    isPreferred: PropTypes.bool,
    fuelLabel: PropTypes.string
  })
};

const AddressContainer = styled.div`
  display: flex;
`;
const AddressText = styled(AddressRegular)`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const MiniCardBody = styled(Flex)`
  flex-direction: column;
  width: 100%;
`;
const VendorPriceSection = styled(Flex)`
  flex-direction: row;
  justify-content: space-between;
`;
const DistanceLocation = styled(Flex)`
  gap: 6px;
  flex-direction: column;
`;
const StyledVendorLogo = styled(CarVendorLogo)`
  margin-bottom: 0px;
  padding-bottom: 4px;
`;
const CarsAvailable = styled.div`
  font-size: 14px;
  gap: 4px;
  padding-bottom: 10px;
  margin-right: 16px;
`;
const VendorLogos = styled(Flex)`
  gap: 12px;
  flex-wrap: wrap;
`;
const CarVendorLocationOptionMini = _ref => {
  let {
    locationData
  } = _ref;
  const {
    address,
    airportCode,
    carsAvailable,
    distance,
    isInPolicy,
    startingPrice,
    vendorCodes,
    pickupShuttle
  } = locationData;
  return /*#__PURE__*/React.createElement(OptionMini, {
    isOutOfPolicy: !isInPolicy
  }, /*#__PURE__*/React.createElement(MiniCardBody, null, /*#__PURE__*/React.createElement(VendorPriceSection, null, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement(VendorLogos, null, vendorCodes.map(vendorCode => /*#__PURE__*/React.createElement(StyledVendorLogo, {
    key: vendorCode,
    size: "20px",
    constrain: "height",
    carCode: vendorCode
  }))), /*#__PURE__*/React.createElement(CarsAvailable, null, /*#__PURE__*/React.createElement("b", null, carsAvailable), " Cars Available", /*#__PURE__*/React.createElement("div", null, vendorCodes.length > 1 ? `from ${vendorCodes.length} vendors` : ''))), /*#__PURE__*/React.createElement(PriceButton, {
    price: startingPrice,
    label: "per day",
    showFrom: true
  })), /*#__PURE__*/React.createElement(DistanceLocation, null, /*#__PURE__*/React.createElement("div", null, distance !== null ? /*#__PURE__*/React.createElement(AreaDistance, {
    distance: distance.toFixed(1)
  }) : null), address && address.length ? /*#__PURE__*/React.createElement(AddressContainer, null, /*#__PURE__*/React.createElement(AddressText, {
    greyPaletteNum: 900
  }, address.filter(el => el).join(', '))) : null), airportCode ? /*#__PURE__*/React.createElement(Box, {
    mt: 3
  }, /*#__PURE__*/React.createElement(AirportShuttle, {
    code: pickupShuttle,
    airportCode: airportCode
  })) : null));
};
CarVendorLocationOptionMini.propTypes = {
  locationData: PropTypes.shape({
    address: PropTypes.arrayOf(PropTypes.string),
    airportCode: PropTypes.string,
    carsAvailable: PropTypes.number,
    distance: PropTypes.number,
    isInPolicy: PropTypes.bool,
    startingPrice: PropTypes.number,
    vendorCodes: PropTypes.arrayOf(PropTypes.string),
    pickupShuttle: PropTypes.string
  })
};

const NoResults = _ref => {
  let {
    onButtonClick,
    noApiResult = false
  } = _ref;
  return /*#__PURE__*/React.createElement(EmptyState, {
    size: "fullWidth",
    iconLineName: "Car",
    secondaryContent: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      py: SPACE.size16
    }, noApiResult ? null : 'Try adjusting your preferences'), /*#__PURE__*/React.createElement(Button, {
      onClick: onButtonClick,
      text: noApiResult ? 'New Search' : 'Clear Filters'
    })),
    text: noApiResult ? 'No cars matched your search' : 'No cars matched your filters'
  });
};
NoResults.propTypes = {
  onButtonClick: PropTypes.func.isRequired,
  noApiResult: PropTypes.bool
};

export { AirportShuttle, CarAddressDisplay, CarOptionCard, CarVendorLocationOptionMini, NoResults };
//# sourceMappingURL=index.esm.js.map
